/**
 * API client for communicating with the backend
 */
import { config } from '../config';
import type { BulkMediaAccount, CategoryOverview, JobDetail, JobSummary, NewJob, NewStagedFile } from '../bulkMedia/types';
import type { SidebarLayout } from '../components/sidebar/sidebarLayout';

// Types - defined first for proper exports
/** A Vault Badge (CONTEXT.md → Badge): the Account's coloured label; `color` is a palette key. */
export interface VaultBadge {
  id: number;
  name: string;
  color: 'red' | 'orange' | 'amber' | 'green' | 'teal' | 'blue' | 'indigo' | 'purple' | 'pink' | 'grey';
  count: number;
}

export interface Account {
  account_id: string;
  model_id: string | null;
  profile_name: string | null;
  username: string | null;
  /** Platform the account lives on (ADR 0019). null/absent ⇒ treat as OnlyFans. */
  platform?: 'onlyfans' | 'fansly' | null;
  persona: string | null;
  is_enabled: boolean;
  /** Kill switch (ADR 0042): emergency stop on all automation. Humans keep working the CRM. */
  kill_switch?: boolean;
  kill_switch_on_at?: string | null;
  kill_switch_off_at?: string | null;
  kill_switch_by?: string | null;
  kill_switch_reason?: string | null;
  telegram_chat_id: string | null;
  timezone?: string | null;
  base_information?: string | null;
  physical_traits?: string | null;
  preferences_and_hobbies?: string | null;
  sexual_fetishes_and_positioning_in_sex?: string | null;
  family_information?: string | null;
  additional_details?: string | null;
  emotional_patterns_and_triggers?: string | null;
  memories?: string | null;
  current_life_context?: string | null;
  conversational_dynamics?: string | null;
  /** Authoritative summary of what content exists on the page. Always injected into the AI persona. */
  content_inventory?: string | null;
  hot_conversational_dynamics?: string | null;
  /** Account-level Offer-framing default (ADR 0056). Drives AUTO mode only —
   *  Fixed/Adaptable author per-step. NULL ⇒ the built-in code default. */
  offer_framing_default?: string | null;
  normal_chat_emojis?: string | null;
  hot_chat_emojis?: string | null;
  /** Per-account message presentation, shared by OnlyFans and Fansly. */
  start_letter_policy?: 'capital' | 'small' | 'mixed';
  speed_policy?: 'quick' | 'balanced' | 'slow';
  /** Soft target number of bubbles per turn, excluding follow-ups. */
  bubble_count_min?: number;
  bubble_count_max?: number;
  /** Firm word limit per bubble; minimum 15. */
  bubble_word_limit?: number;
  /** Plan steps for new-fan opener; persisted as JSON on the account. */
  start_with_new_fan_setup?: Array<{
    heading?: string;
    description?: string;
    message_example?: string;
    /** Some configs use this long key; UI normalizes to `message_example` on save. */
    'message_example (can be split into separate messages if needed)'?: string;
  }> | null;
  ai_working_scope?: 'all_folders' | 'specific_folders' | null;
  ai_selected_folders?: string[] | null;
  ai_base_regime?: string | null;
  ai_call_chatter_on_completion?: boolean | null;
  ai_hot_transition_mode?: 'horny' | 'moderate' | 'shy' | 'custom' | null;
  ai_hot_transition_custom_prompt?: string | null;
  /** When the guided AI setup wizard was last completed. null = never (shows the
   *  "Finish setup" entry on the account page). */
  ai_setup_completed_at?: string | null;
  is_teasing_content_allowed?: boolean;
  is_life_content_allowed?: boolean;
  is_sales_content_allowed?: boolean;
  is_first_ppv_content_allowed?: boolean;
  /** Master switch for fixed (operator-pinned) sales ladders (ADR 0005). */
  is_fixed_ladder_active?: boolean;
  /** Master switch for the auto (fully-automated) sales ladder (ADR 0023). The
   *  AI builds and runs the ladder itself; mutually exclusive with fixed
   *  (precedence fixed > auto > llm_curated). */
  is_auto_ladder_active?: boolean;
  is_logged_in?: boolean;
  lists_to_ignore?: string[];
  /** OnlyFans list name the AI writes to after an interrupt. NULL/empty → "Notifications". */
  interrupt_list_name?: string | null;
  default_automation_regime?: string;
  /** Legacy column; mirrored when saving AI chatter toggle. */
  call_chatter_on_regime_completion?: boolean | null;
  /** Resolved prompt for hot-branch escalation (synced from mode / custom). */
  hot_transition_prompt?: string | null;
  /** FK to hot_transition_regimes; source of truth for hot transition preset after refresh. */
  hot_transition_regime_id?: number | null;
  avatar_url?: string | null;
  ignore_detection_time?: number | null;
  allow_follow_up_messages?: boolean | null;
  follow_up_messages_count?: number | null;
  follow_up_messages_time_min?: number | null;
  follow_up_messages_time_max?: number | null;
  follow_up_2_time_min?: number | null;
  follow_up_2_time_max?: number | null;
  follow_up_3_time_min?: number | null;
  follow_up_3_time_max?: number | null;
  /** When true, each delivered follow-up is unsent after follow_up_delete_after_seconds. */
  auto_delete_follow_ups?: boolean | null;
  /** Seconds a delivered follow-up lives before being auto-unsent. UI edits this in minutes. */
  follow_up_delete_after_seconds?: number | null;
  is_fan_info_in_notifications_allowed?: boolean | null;
  allowed_fan_info_notification_fields?: string[] | null;
  is_user_info_short_analysis_allowed?: boolean | null;
  is_comment_updating_allowed?: boolean | null;
  is_comment_overwrite_allowed?: boolean | null;
  allowed_comment_fields?: string[] | null;
  /** Language client-facing Telegram notifications render in ('en' | 'ru' | 'uk'). */
  notification_language?: 'en' | 'ru' | 'uk' | null;
  /**
   * Telegram notification delivery health, written by the notification sender and
   * the Send-test endpoint; read-only in the UI.
   * 'ok' | 'migrated' | 'bot_not_in_group' | 'unconfigured' | 'unknown'.
   */
  telegram_health?: 'ok' | 'migrated' | 'bot_not_in_group' | 'unconfigured' | 'unknown' | null;
  telegram_health_detail?: string | null;
  telegram_health_updated_at?: string | null;
  /** Chat-tracking list (ADR 0016): how the AI adds actively-chatted fans to a list.
   *  'off' | 'daily' (dd.mm) | 'custom' (single named bucket). */
  chat_tracking_mode?: 'off' | 'daily' | 'custom' | null;
  /** Custom-mode bucket list name. */
  chat_tracking_custom_name?: string | null;
  /** Master switch for the daily cleanup that DELETES stale tracking lists. */
  chat_tracking_cleanup_enabled?: boolean | null;
  /** Retention window in days behind today (keeps today + prior N). */
  chat_tracking_cleanup_after_days?: number | null;
  /** Custom bucket creation time; managed by bigdayz.new, read-only in the UI. */
  chat_tracking_custom_created_at?: string | null;
  /** Turn-threshold flag (ADR 0054): flag a long chat once its session reaches N AI
   *  turns. null/0 = off. The list name MAY be an ignore list (emergent hand-off);
   *  empty list + notify on = notify-only. Read by bigdayz.new. */
  turn_threshold?: number | null;
  turn_threshold_notify?: boolean | null;
  turn_threshold_list_name?: string | null;
  /** Notification format (ADR 0030): 'verbose' (default) or 'compact'. */
  notification_format?: 'verbose' | 'compact' | null;
  /** Activity opt-outs. true/null = deliver the ping; false = mute. Delivery-only:
   *  every flag here is notification-only and never changes what the fan sees.
   *  `notify_fan_requests` used to live here and broke that rule (it muted the ping
   *  for an interrupt that still handed the chat over), so it is no longer read or
   *  written — fan-request alerts follow the interrupt's own on/off switch. */
  notify_ppv_sent?: boolean | null;
  notify_free_content_sent?: boolean | null;
  notify_tip_ask?: boolean | null;
  notify_vip_offer_sent?: boolean | null;
  notify_chat_ended?: boolean | null;
  /** Opt-in (ADR 0053): notify on ALL payment events, incl. manual/unsolicited. Default false. */
  notify_all_payment_events?: boolean | null;
  /** AI Work schedule (ADR 0043): when the AI may reply, on the operator's clock. */
  schedule_mode?: 'always_on' | 'scheduled' | null;
  schedule_timezone?: string | null;
  work_schedule?: WorkWindow[] | null;
}

/** One AI Work window (ADR 0043). Times are minutes from local midnight (0..1440);
 *  end<=start wraps past midnight and the window belongs to its START day.
 *  day: 0=Mon … 6=Sun. regime null = fall back to the account default regime. */
export interface WorkWindow {
  day: number;
  start: number;
  end: number;
  regime?: string | null;
  /** ADR 0047: per-window ignore-list override. A FULL REPLACE of the account's
   *  lists_to_ignore while this window is active. null/absent = inherit the
   *  account default; [] = silence no one; [names] = ignore exactly those. */
  ignore_lists?: string[] | null;
  /** ADR 0047: per-window interrupt on/off overrides — a PARTIAL map keyed by
   *  interrupt name (e.g. "fan_not_happy"). A key present forces that interrupt
   *  on/off while the window is active; an absent key inherits. */
  interrupts?: Record<string, boolean> | null;
  /** ADR 0055: per-window request-mode overrides. Each forces that branch's mode
   *  while the window is active; null/absent = inherit the account policy. */
  content_mode?: 'handoff' | 'automate' | null;
  custom_mode?: 'handoff' | 'automate' | 'decline' | null;
  call_mode?: 'handoff' | 'automate' | 'decline' | null;
  /** ADR 0055: per-window PPV offers on/off; null/absent = inherit is_active. */
  ppv_active?: boolean | null;
  /** Per-window "call chatter on completion"; null/absent = inherit the account. */
  call_chatter?: boolean | null;
}

export interface AccountSubscriptionStatus {
  active: boolean;
  /** Status of the live Subscription row (active / trialing / past_due); null when none. */
  status?: string | null;
  days_left: number | null;
  plan_code?: string | null;
  /** Authoritative subscription end instant (ISO). Display this rather than deriving from days_left. */
  current_period_end?: string | null;
  /** The account's billing mode (ADR 0012). 'turn' = On demand: no Subscription row, so
   *  `active` is false, but the account is billed per response rather than planless. */
  billing_mode?: BillingMode;
  /** True when billing paused the account (e.g. the payer's wallet ran out). */
  billing_suspended?: boolean;
}

export interface CreateAccountData {
  // Both optional now: the server generates a random-word account_id when omitted,
  // and an account may be created without a model (linked lazily on first connect).
  account_id?: string;
  model_id?: string;
  profile_name?: string;
  username?: string;
  persona?: string;
  // Platform the account lives on (ADR 0019). Absent ⇒ OnlyFans.
  platform?: 'onlyfans' | 'fansly';
}

export interface AccountConnectionStatus {
  is_logged_in: boolean;
  username?: string | null;
  profile_name?: string | null;
  avatar_url?: string | null;
}

// --- Streamed login (see ../../streamed-login-agent) ---
export interface ProxyPoolRow {
  id: string;
  label?: string | null;
  created_at: string;
}
export interface ProxyPoolInfo {
  enabled: boolean;
  account_has_proxy: boolean;
  available: ProxyPoolRow[];
}
export interface SourceProxyRequest {
  mode: 'auto' | 'pick' | 'manual';
  pool_id?: string | null;
  proxy_url?: string | null;
}

/* ── Sandbox Chats (ADR 0066, bigdayz.new) ────────────────────────────
   A fabricated Sandbox Fan inside a real Account, answered by the real turn.
   Shapes mirror the brain's /sandbox API, which /api/sandbox/* proxies. */

/** How fast a Sandbox turn answers: the Account's real pacing, or none of it. */
export type SandboxPacing = 'account' | 'max_speed';

export interface SandboxFanProfile {
  name?: string;
  username?: string;
  displayName?: string;
  notice?: string;
  is_performer?: boolean;
  /** Console-only: the starting point the chat was created from. */
  preset?: string;
}

export interface SandboxListRef {
  id: string | null;
  name: string;
}

export interface SandboxChatSummary {
  account_id: string;
  fan_id: string;
  created_by: string;
  profile: SandboxFanProfile;
  lists: SandboxListRef[];
  pacing: SandboxPacing;
  created_at: string;
}

/** One Sandbox transcript row. `system` rows record what the turn would have
 *  done outside the chat (kind: notification | list_op | mark_unread | fan_note). */
export interface SandboxMessage {
  id: number;
  sender: 'fan' | 'model' | 'system';
  kind?: string | null;
  text?: string | null;
  media?: Array<{ id: string; type?: string | null }> | null;
  price?: number | string | null;
  is_purchased?: boolean | null;
  purchased_at?: string | null;
  preview_ids?: string[] | null;
  tip_amount?: number | string | null;
  payload?: Record<string, unknown> | null;
  deleted_at?: string | null;
  /** The Sandbox turn that wrote this row (model / system rows); keys its trace. */
  turn_id?: string | null;
  created_at: string;
}

/** One LLM call a Sandbox turn made — the Sandbox trace, shown to super admins only. */
export interface SandboxLlmCall {
  id: number;
  turn_id: string | null;
  seq: number | null;
  node: string | null;
  requested_model: string | null;
  served_model: string | null;
  request_messages: Array<{ role?: string | null; content?: unknown; tool_calls?: unknown; tool_call_id?: string; name?: string }>;
  params?: Record<string, unknown> | null;
  response?: { content?: unknown; reasoning?: string; tool_calls?: unknown; metadata?: unknown } | null;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
  latency_ms?: number | null;
  error?: string | null;
  created_at: string;
}

export interface SandboxChat {
  account_id: string;
  fan_id: string;
  created_by: string;
  profile: SandboxFanProfile;
  lists: SandboxListRef[];
  pacing: SandboxPacing;
  turn_in_progress: boolean;
  follow_up_pending: boolean;
  messages: SandboxMessage[];
}

export interface SandboxSeedMessage {
  sender: 'fan' | 'model';
  text?: string;
  price?: number;
  purchased?: boolean;
  tip?: number;
}

export interface Model {
  model_id: string;
  model_name: string;
}

/** Signup role: what the person says they'll do. Only an owner owes a Telegram connection. */
export type SignupRole = 'owner' | 'chatter';

export interface TelegramConnection {
  required: boolean;
  connected: boolean;
  username: string | null;
  connected_at: string | null;
  unreachable: boolean;
}

export interface User {
  id: string;
  email: string;
  role: string;
  username?: string;
  created_at?: string;
}

export interface UserWithAccess extends User {
  model_access: string[];
}

export interface UserLimits {
  max_owned_accounts: number | null;
  max_chatters: number | null;
}

export interface UserWithLimits extends User {
  max_owned_accounts: number | null;
  max_chatters: number | null;
  current_owned_accounts: number;
  current_chatters: number;
}

export interface ApiKeyInfo {
  has_key: boolean;
  key_prefix?: string;
  created_at?: string;
  last_used_at?: string;
}

export interface GenerateApiKeyResponse {
  api_key: string;
  message: string;
  key_prefix: string;
}

/** A key for the Public API (api.onlymindai.com) — not the Console key above. ADR 0041. */
export interface PublicApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  active: boolean;
}

/** Returned once, on create: the only time the key itself is ever sent. */
export interface CreatedPublicApiKey extends PublicApiKey {
  key: string;
}

export interface PublicApiScope {
  scope: string;
  available: boolean;
}


export interface AccountPermissions {
  owner_access_to_account: boolean;
  access_to_account: boolean;
  access_chat_assistant: boolean;
  access_assistant_setup: boolean;
  access_assistant_content: boolean;
  access_model_info: boolean;
  access_notifications_settings: boolean;
  access_mass_messages: boolean;
  access_mass_messages_lists: boolean;
  access_mass_messages_campaigns: boolean;
  access_mass_messages_schedules: boolean;
  access_priority_mass_messages: boolean;
  access_priority_mass_messages_lists: boolean;
  access_priority_mass_messages_campaigns: boolean;
  access_priority_mass_messages_schedules: boolean;
  access_automated_messages: boolean;
  access_automated_messages_setup: boolean;
  access_token: boolean;
  access_pay: boolean;
  access_censure: boolean;
  access_stats: boolean;
  access_reconnect: boolean;
  access_audio_settings: boolean;
  access_bulk_media: boolean;
  access_dynamic_lists: boolean;
  access_audio: boolean;
  access_tracking_links: boolean;
  access_permission_management: boolean;
}

/** ADR 0031: which Tools this login may open. `launched` = open to this login at all;
 *  `usable` = launched and allowed on at least one Account (Copy: two Accounts). */
export interface MyTools {
  launched: { bulk_media: boolean; audio: boolean; dynamic_lists: boolean; tracking_links: boolean };
  usable: { copy: boolean; bulk_media: boolean; audio: boolean; dynamic_lists: boolean; tracking_links: boolean };
  /** Accounts each Mass messages page offers (its tab's permission); null = every Account. */
  mass_pages?: { mass_messages: string[] | null; priority_mass_messages: string[] | null; automated_messages?: string[] | null; chat_assistant?: string[] | null };
  /** Accounts the Dynamic lists / Tracking links pages offer (their Permission flag); null = every Account. */
  tool_accounts?: { dynamic_lists: string[] | null; tracking_links: string[] | null };
}

/** PPV voice notes (bigdayz.new ADR 0068) — the Audio page. A voice is a label and a
 *  state only: the provider never shows up here (white-label). */
export type VoiceKind = 'stock' | 'custom';
export interface Voice {
  id: string;
  kind: VoiceKind;
  label: string;
  languages: string[];
  status: 'processing' | 'ready' | 'failed';
  is_default: boolean;
  created_at?: string | null;
  /** One of your own custom voices (you may delete it). False for stock voices and for
   *  someone else's voice an Account already uses (ADR 0028). */
  owned?: boolean;
}
export interface VoicesResponse {
  voices: Voice[];
  custom_count: number;
  custom_limit: number;
  can_clone: boolean;
  voice_service_ready: boolean;
}
export interface AudioPolicyBody {
  voice_id: string | null;
  custom_voice_id: string | null;
  voice_notes_enabled: boolean;
  /** PPV sends without a voice note between two that carry one. null / 0 = every PPV. */
  voice_note_cooldown: number | null;
  normal_tone_notes: string;
  hot_tone_notes: string;
  allowed_languages: string[];
}
export interface AudioPolicy extends AudioPolicyBody {
  exists: boolean;
  updated_at?: string | null;
}
export interface VoiceNoteQuota {
  allowed: number;
  used: number;
  remaining: number;
}
export type VoiceRegister = 'normal' | 'hot';
export interface VoiceNoteTestBody {
  /** Her text, spoken as is: the AI only adds delivery tags. */
  text: string;
  voice_register: VoiceRegister;
  voice_id: string | null;
  custom_voice_id: string | null;
  normal_tone_notes: string;
  hot_tone_notes: string;
  allowed_languages: string[];
}
export interface VoiceNoteTestResult {
  outcome: 'sent' | 'skipped';
  skip_reason: string | null;
  language: string;
  /** Tagged script that was spoken — super admins only, empty for everyone else. */
  script: string;
  spoken: string;
  audio_b64: string | null;
  mime: string | null;
  duration_s: number | null;
  quota: VoiceNoteQuota;
}

/** Voices page (console ADR 0028): every voice you can wire, and your Accounts' voices. */
export interface LibraryVoice extends Voice {
  /** How many of the Accounts you can set use this voice. */
  account_count: number;
}
export interface VoiceLibrary {
  voices: LibraryVoice[];
  custom_count: number;
  custom_limit: number;
  can_clone: boolean;
  voice_service_ready: boolean;
}
export interface VoiceRef {
  kind: VoiceKind;
  id: string;
  label: string;
  owned: boolean;
  status: Voice['status'];
}
export interface VoiceAccount {
  account_id: string;
  name: string;
  username: string;
  platform: string;
  voice: VoiceRef | null;
  voice_notes_enabled: boolean;
  quota: VoiceNoteQuota;
}
/** Create audio in the chat window. */
export interface AccountVoice {
  voice: VoiceRef | null;
  quota: VoiceNoteQuota;
  voice_service_ready: boolean;
}
export interface CreatedAudio {
  recording_id: number | null;
  audio_b64: string;
  mime: string;
  duration_s: number;
  spoken: string;
  script: string;
  quota: VoiceNoteQuota;
}

/**
 * Statistics — summed daily chat-stats counters over a date range.
 * The write side (bigdayz.new, ADR 0017) records these per account per Kyiv day;
 * `life_content_sent` and `teasing_sent` are deliberately not exposed.
 */
export interface ChatStatsTotals {
  /** Distinct AI responses (one per reply, whatever the bubble count). */
  turns: number;
  messages_sent: number;
  follow_ups_sent: number;
  chats_activated: number;
  new_chats: number;
  tip_asks_hot: number;
  tip_asks_normal: number;
  tip_asks_paid_sexting: number;
  // Received tips are unattributed on purpose — never split by branch.
  tips_received_count: number;
  tips_received_sum: number;
  ppv_sent_sales_ladder: number;
  ppv_sent_content_request: number;
  ppv_purchased_count: number;
  ppv_purchased_sum: number;
  vip_offers_sent: number;
  custom_requests_processed: number;
  custom_requests_paid: number;
  custom_requests_paid_sum: number;
  vc_requests_processed: number;
  vc_requests_paid: number;
  vc_requests_paid_sum: number;
}

/** One totals row per calendar day in the range, zero-filled — feeds the sparklines. */
export interface ChatStatsDay extends ChatStatsTotals {
  date: string;
}

/** One totals row per account in the fleet — drives the leaderboard. */
export interface ChatStatsAccount extends ChatStatsTotals {
  account_id: string;
  /** Display name (username, falling back to profile name, then the id). */
  name: string;
}

export interface ChatStatsInterrupt {
  name: string;
  count: number;
}

/* ── Unified Statistics (GET /statistics) ─────────────────────────── */

/** A money figure both ways: what fans paid, and what the creator keeps. */
export interface StatPair {
  gross: number;
  net: number;
}

/** Who made a sale sold in chat, plus the rest of Revenue ("not sold in chat"). */
export type StatSeller = 'ai' | 'team' | 'unknown' | 'not_in_chat';
/** The Sellers a single chat sale can have. */
export type StatCreditedSeller = 'ai' | 'team' | 'unknown';

export interface StatisticsDay {
  /** UTC calendar day, YYYY-MM-DD. */
  day: string;
  revenue: StatPair;
  /** Revenue type → money; only types that earned something that day. */
  by_type: Record<string, StatPair>;
  sellers: Record<StatSeller, StatPair>;
  ppv_sold: Record<StatCreditedSeller, number>;
  /** PPVs + tips paid, by Seller. */
  payments: Record<StatCreditedSeller, number>;
}

export interface StatisticsAccountMoney {
  account_id: string;
  name: string;
  revenue: StatPair;
  sellers: Record<StatSeller, StatPair>;
  ppv_sold: Record<StatCreditedSeller, number>;
}

export interface StatisticsMoney {
  revenue: StatPair & { by_type: (StatPair & { type: string })[] };
  sellers: Record<StatSeller, StatPair>;
  days: StatisticsDay[];
  /** Revenue of the same number of days right before the range. */
  previous: StatPair;
  ppv_sold: Record<StatCreditedSeller, { count: number; gross: number }>;
  accounts: StatisticsAccountMoney[];
  /** In-scope Fansly accounts (the Accounts tab labels and filters by platform). */
  fansly_account_ids: string[];
}

export interface StatisticsTeamRow {
  operator_id: string;
  operator_username: string;
  account_ids: string[];
  /** account_id → gross sales (PPVs + tips) there. */
  sales_by_account: Record<string, number>;
  total_sales: number;
  ppv_sales: number;
  tips: number;
  ppv_sold: number;
  ppv_sent: number;
  avg_sent_ppv_price: number;
  avg_sold_ppv_price: number;
  avg_ppv_purchase_seconds: number;
  total_messages: number;
  chats_count: number;
  words: number;
  word_tracked_messages: number;
  free_media_sent: number;
}

export interface StatisticsTeamDay {
  day: string;
  messages: number;
  chats: number;
}

export interface StatisticsTeamAccount {
  account_id: string;
  operators: number;
  messages: number;
  chats: number;
  ppv_sent: number;
  ppv_sold: number;
}

/** Response times of one responder group (CONTEXT.md "Response time"). */
export interface StatisticsResponseTimes {
  count: number;
  median_seconds: number | null;
  avg_seconds: number | null;
  /** Responses within 5 minutes. */
  quick: number;
}

export interface StatisticsResponses {
  all: StatisticsResponseTimes;
  ai: StatisticsResponseTimes;
  team: StatisticsResponseTimes;
  /** operator id → that operator's responses. */
  operators: Record<string, StatisticsResponseTimes>;
}

export interface StatisticsResponse {
  range: { start: string; end: string };
  money: StatisticsMoney;
  team: StatisticsTeamRow[];
  team_days: StatisticsTeamDay[];
  team_accounts: StatisticsTeamAccount[];
  responses?: StatisticsResponses;
}

export interface ChatStatsResponse {
  range: { start: string; end: string };
  totals: ChatStatsTotals;
  days: ChatStatsDay[];
  accounts: ChatStatsAccount[];
  interrupts: ChatStatsInterrupt[];
}

/* ── Dashboard layout (ADR 0030) ──────────────────────────────────── */

/** One dashboard widget as stored: its size in grid cells and its settings. */
export interface DashboardWidgetDoc {
  id: string;
  type: string;
  w: number;
  h: number;
  cfg: Record<string, unknown>;
}

/** The person's Dashboard layout. No `widgets` = still the default layout. */
export interface DashboardLayoutDoc {
  v: number;
  money: 'gross' | 'net';
  widgets?: DashboardWidgetDoc[];
}

// Global Team Management Types (new system)
export interface GlobalTeamMember {
  user_id: string;
  email: string;
  username?: string;
  added_at: string;
  assigned_accounts_count: number;
}

/** The client CRM's permissions, granted from Team (CONTEXT.md: CRM permission). */
export type CrmPermission = 'chats' | 'vault' | 'collections' | 'queue' | 'mass' | 'profile' | 'statements' | 'earnings';
/** Permission level: Full = view and act; View only = see, change nothing. */
export type CrmLevel = 'full' | 'view';
/** One Per-subscriber access entry — narrows Chats for one fan. */
export interface CrmFanAccess { fan_id: string; level: CrmLevel | 'none' }
export interface CrmAccess {
  permissions: Partial<Record<CrmPermission, CrmLevel>>;
  fans: CrmFanAccess[];
  /** Media download (CONTEXT.md): save the account's media out of the CRM.
      Only held alongside a CRM permission; absent from older servers. */
  media_download?: boolean;
}

/** What a Team Role grants: Permission flags plus CRM permissions with their level. */
export interface TeamRoleGrants {
  flags: (keyof AccountPermissions)[];
  crm: Partial<Record<CrmPermission, CrmLevel>>;
  /** Media download; absent from older servers (= off). */
  media_download?: boolean;
}

/** A Team Role (CONTEXT.md: Role) — a fleet's named permission set. People
    linked to it follow it: editing it updates their grants.
    `id` is the starting Role's key ('full', 'manager', …) or a uuid. */
export interface TeamRole {
  id: string;
  key: string | null;
  name: string;
  description: string;
  grants: TeamRoleGrants;
  builtin: boolean;
  /** Full: always every permission, cannot be edited or deleted. */
  locked: boolean;
}

export interface TeamRoleInput { name: string; description: string; grants: TeamRoleGrants }

/** How a saved Role edit reached the people linked to it. */
export interface TeamRoleSync { updated: number; failed: number }

export interface AccountAssignment {
  account_id: string;
  account_name: string;
  permissions: AccountPermissions;
  /** Absent from servers that predate CRM permissions. */
  crm?: CrmAccess;
  /** The fleet's Role the person is linked to here; null/absent = Custom. */
  role_id?: string | null;
}

/** One person's access on one account (the account's Access view). */
export interface AccountMember {
  user_id: string;
  email: string;
  username?: string | null;
  permissions: AccountPermissions;
  crm?: CrmAccess;
}

export interface DashboardStatistics {
  total_accounts: number;
  active_accounts: number;
  team_size: number;
}

export interface PaymentsAccountRow {
  account_id: string;
  profile_name?: string | null;
  username?: string | null;
  avatar_url?: string | null;
  subscription_active: boolean;
  subscription_status: string;
  subscription_period_end?: string | null;
  subscription_plan_id?: string | null;
  subscription_plan_code?: string | null;
  subscription_plan_name?: string | null;
  assistant_price_cents: number;
  no_assistant_price_cents: number;
  default_checkout_plan_code?: string | null;
  auto_renew_enabled?: boolean;
  auto_renew_plan_code?: string | null;
  price_cents: number;
  /** Coexisting billing modes (ADR 0012): 'metered' (price buys included turns, overage bills
   *  extra), 'turn' (pay per assistant turn), 'unlimited' (admin-only operator flat plan), or
   *  the retired flat 'subscription' still present on transitional accounts. */
  billing_mode?: BillingMode;
  /** This account's on-demand Turn rate in micros (ADR 0015), tiered off its monthly
   *  balance so it differs per account. Null when it could not be determined.
   *  1 cent = 10000 micros. */
  turn_rate_micros?: number | null;
  /** The user whose wallet on-demand charges are drawn from — often not the viewer. */
  payer_username?: string | null;
  /** Previous-month gross earnings, the figure the turn rate is tiered on. */
  monthly_balance_usd?: number | null;
  /** True when a turn-billed account is paused because the payer's Wallet ran out. */
  billing_suspended?: boolean;
  /** Platform: 'onlyfans' | 'fansly'. Both support the on-demand "Get price"
   *  button; it selects the platform's earnings gateway (ADR 0013). */
  platform?: string | null;
}

/** Result of the on-demand "Get price" extraction (ADR 0013). */
export interface RefreshPriceResponse {
  account_id: string;
  assistant_price_cents: number;
  priced: boolean;
}

/** Coexisting billing modes (ADR 0012). `metered` = price buys Included turns, overage bills
 *  extra; `turn` = pure on-demand (ADR 0007); `unlimited` = admin-only operator flat plan.
 *  `subscription` is the retired flat plan — still present on transitional accounts. */
export type BillingMode = 'metered' | 'turn' | 'unlimited' | 'subscription';

/** Full-automation sales-ladder strategy (ADR 0023): the composition photo+video
 *  bundle ladder (default) or the legacy per-clip video-only engine. */
export type LadderStrategy = 'bundles' | 'video_only';

export interface BillingModeResult {
  account_id: string;
  billing_mode: string;
  billing_suspended: boolean;
  /** This account's own Turn rate in USD (ADR 0015). Null when it cannot be determined. */
  turn_rate_usd: number | null;
  wallet_balance_cents: number;
}

export interface MeteredUsageAccount {
  account_id: string;
  profile_name?: string | null;
  username?: string | null;
  avatar_url?: string | null;
  billing_mode: string;
  /** The Subscription price that funds the Included block, in cents. */
  price_cents: number;
  /** The list per-turn rate (base, or the account's special-rate pin), in USD — what
   *  Included is worked out from, so it never moves with the Volume discount. */
  list_rate_usd: number;
  /** The overage rate actually charged: the list rate less the Payer's Volume discount. */
  rate_usd: number;
  /** Included turns the price buys (L = price ÷ list rate). */
  included_turns: number;
  used_this_period: number;
  overage_turns: number;
  /** Real overage money charged this period, in cents. */
  overage_charged_cents: number;
  /** Dry-run "would-be" overage while metered_live is off, in cents. */
  would_be_overage_cents: number;
  overage_mode: 'continue' | 'hard_stop';
  /** False = dry-run (meter shows, nothing debits). */
  metered_live: boolean;
  period_end?: string | null;
  suspended: boolean;
}

/** One step of the Volume discount ladder (ADR 0038). */
export interface VolumeLadderStep {
  min_accounts: number;
  bps: number;
}

/** The viewer's Volume discount as a Payer: a percentage off their Subscription charges set by
 *  how many Accounts they pay a Subscription for, plus the Next-tier hint. */
export interface VolumeDiscountSummary {
  bps: number;
  paid_count: number;
  /** `override` = a special rate set by an operator; `off` = the kill switch is on. */
  source: 'ladder' | 'override' | 'off';
  next_tier: { accounts_needed: number; bps: number } | null;
  /** What the next tier saves per month on top of the current one, in cents. */
  saving_cents: number;
  ladder: VolumeLadderStep[];
}

export interface MeteredUsage {
  wallet_balance_cents: number;
  accounts: MeteredUsageAccount[];
  total_included_turns: number;
  total_used_this_period: number;
  total_overage_charged_cents: number;
  total_would_be_overage_cents: number;
  volume_discount?: VolumeDiscountSummary;
  /** The list rate before any discount, in USD. */
  base_rate_usd?: number;
}

export interface TurnBillingUsageAccount {
  account_id: string;
  profile_name?: string | null;
  username?: string | null;
  avatar_url?: string | null;
  /** Real money charged for this account (settled debits), in cents. */
  amount_charged_cents: number;
  responses_billed: number;
  /** Accrued but not-yet-swept spend, in cents (may be fractional). */
  pending_cents: number;
  /** Turn count backing pending_cents — nonzero before it ever produces a settled debit. */
  pending_responses: number;
  suspended: boolean;
  /** This account's own Turn rate in USD — tiered off its previous-month gross (ADR 0015),
   *  frozen for the Kyiv billing day. Null when the gross read failed. */
  rate_usd?: number | null;
  /** What this account's tier resolves to now. Differs from `rate_usd` only while the
   *  billing day is frozen at an older rate (most visibly at the M3 cutover). */
  tier_rate_usd?: number | null;
  /** An operator override is in force, so the rate is not read off the ladder. */
  rate_pinned?: boolean;
  /** Previous-month gross the rate tiers on. Null = unknown, so `rate_usd` is the
   *  bottom-tier assumption rather than a tiered rate. */
  monthly_balance_usd?: number | null;
  /** Whether "Get price" can extract this account's monthly balance (OnlyFans only). */
  can_get_price?: boolean;
}

/** One rung of the on-demand rate ladder (ADR 0015), served from the same functions
 *  that bill so the table can never drift from what is charged. */
export interface TurnRateLadderTier {
  min_gross: number;
  /** null on the open-ended top rung. */
  max_gross: number | null;
  plan_price_usd: number;
  rate_usd: number;
  /** Monthly responses above which the subscription is the cheaper mode. */
  crossover_turns: number;
}

export interface TurnBillingUsage {
  /** The BOTTOM of the rate ladder — the "from $X per response" figure. There is no single
   *  fleet-wide rate since ADR 0015; per-account rates are on each row's `rate_usd`. */
  turn_rate_usd: number;
  rate_ladder?: TurnRateLadderTier[];
  wallet_balance_cents: number;
  accounts: TurnBillingUsageAccount[];
  total_charged_cents: number;
  total_responses: number;
  total_pending_cents: number;
  total_pending_responses: number;
}

export interface TurnBillingSweepResult {
  accounts: number;
  billed: number;
  charged_cents: number;
  suspended: string[];
  unsuspended: string[];
}

export interface PaymentsLedgerRow {
  id: string;
  amount_cents: number;
  currency: string;
  entry_type: string;
  created_at?: string | null;
  reference_type?: string | null;
  reference_id?: string | null;
  metadata: Record<string, unknown>;
}

export interface PaymentsSummary {
  wallet_balance_cents: number;
  wallet_currency: string;
  plan_id: string;
  plan_code: string;
  plan_name: string;
  plan_monthly_price_cents: number;
  plan_features: string[];
  available_plan_codes: string[];
  accounts: PaymentsAccountRow[];
  ledger: PaymentsLedgerRow[];
  info_hint: string;
  /** Backend sets true when NOWPayments sandbox + top-up is configured; optional sandbox_case on create invoice. */
  nowpayments_sandbox_case_available?: boolean;
  volume_discount?: VolumeDiscountSummary;
}

export interface ReferralLadderTier {
  min_count: number;
  rate_bps: number;
}

export interface ReferralEconomics {
  discount_bps: number;
  window_days: number;
  ladder: ReferralLadderTier[];
  paying_account_count: number;
  current_rate_bps: number;
}

export interface ReferralEarnings {
  total_accrued_cents: number;
  unpaid_balance_cents: number;
  paid_out_cents: number;
  accrual_count: number;
  referred_user_count: number;
  paying_account_count: number;
}

export interface ReferralAccrualRow {
  id: string;
  amount_charged_cents: number;
  rate_bps: number;
  commission_cents: number;
  currency: string;
  paying_account_count: number;
  paid: boolean;
  created_at?: string | null;
}

export interface ReferralSummary {
  code: string;
  payout_wallet_address?: string | null;
  payout_wallet_network: string;
  economics: ReferralEconomics;
  earnings: ReferralEarnings;
  accruals: ReferralAccrualRow[];
}

export interface SubscriptionNotificationSettings {
  /** Telegram group ID for subscription-expiry notices (null until connected). */
  subscription_chat_id: string | null;
  /** Owner-chosen advance reminders, in days before expiry. Subset of [1,3,7]. */
  advance_days: number[];
}

export interface TopUpInvoice {
  payment_id: string;
  amount_cents: number;
  currency: 'USDT' | 'USDC';
  pay_amount?: number | null;
  pay_currency?: string | null;
  network: string;
  wallet_address: string;
  qr_payload: string;
  expires_at: string;
  info_hint: string;
  /** 'tron' = our own gateway: the User's permanent address, any amount sent is credited. */
  provider?: string | null;
}

export interface SubscriptionCheckoutResult {
  charged_cents: number;
  discount_cents: number;
  final_total_cents: number;
  wallet_balance_cents_after: number;
  subscribed_account_ids: string[];
  skipped_account_ids: string[];
  promo_applied: boolean;
  info_hint: string;
  volume_discount_bps?: number;
  volume_paid_count?: number;
}

export interface UpgradeAssistantResult {
  account_id: string;
  from_plan_code: string;
  to_plan_code: string;
  days_left: number;
  credit_cents: number;
  assistant_price_30d_cents: number;
  charged_cents: number;
  wallet_balance_cents_after: number;
  new_period_end: string;
  info_hint: string;
}

export interface AutoRenewResult {
  account_id: string;
  auto_renew_enabled: boolean;
  auto_renew_plan_code?: string | null;
  wallet_balance_cents: number;
}

export interface Content {
  content_id: string;
  account_id: string;
  type: 'photo' | 'video';
  description: string | null;
  created_at?: string | null;
  phase_tags: string[];
  topic_tags: string[];
}

export interface ContentUpdateRequest {
  id: number;
  account_id: string;
  timestamp: string;
  is_satisfied: boolean;
}

// New extraction-system types
export interface OfVault {
  id: string;
  vault_name: string;
  account_id: string;
}

/** API phase tags for assistant vaults and extraction (PPV tab uses first_ppv). */
export type AssistantPhaseTag = 'teasing' | 'life' | 'first_ppv';

export interface VaultContentPhase {
  account_id: string;
  vault_id: string;
  phase_tag: AssistantPhaseTag;
  vault_name?: string | null;
}

export interface OfContent {
  id: string;
  thumb_url: string | null;
  full_url?: string | null;
  preview_url?: string | null;
  squarePreview_url?: string | null;
  account_id: string;
  description: string | null;
  type: string | null;
  created_at: string | null;
  vault_names: string[];
  sexting_pack_id?: number | null;
  default_price?: number | null;
  min_price?: number | null;
  max_price?: number | null;
  usage_guidelines?: string | null;
  // Per-item Pinned price (ADR 0042): an exact price the on-demand grid charges for THIS
  // item wherever it composes it, plus an optional firm floor. null ⇒ not pinned.
  pinned_price?: number | null;
  pinned_floor?: number | null;
}

export interface ContentExtractionRequest {
  id: number;
  account_id: string;
  vault_id: string;
  phase_tag?: string | null;
  status: string;
  error_message?: string | null;
  created_at?: string | null;
}

export interface DescriptionGenerationQuota {
  account_id: string;
  allowed_generations: number;
  used_generations: number;
  remaining_generations: number;
}

export interface ScheduleUpdateRequest {
  id: number;
  account_id: string;
  campaign_id: number;
  is_priority: boolean;
  status: 'pending' | 'in_process' | 'satisfied' | 'failed';
  error_message?: string | null;
  created_at?: string | null;
  satisfied_at?: string | null;
}

export interface OfList {
  account_id: string;
  list_uuid: string;
  of_list_id: string;
  of_list_name: string;
  status?: string | null;
}

/** Per-list interrupt columns on list_regime_mapping (null = inherit account default). */
export type FolderAiInterruptOverrideFields = {
  is_fan_asks_for_content_enabled?: boolean | null;
  is_fan_asks_about_catalog_enabled?: boolean | null;
  is_fan_wants_to_buy_content_enabled?: boolean | null;
  is_fan_awaiting_media_enabled?: boolean | null;
  is_fan_asks_for_custom_content_enabled?: boolean | null;
  is_fan_asks_for_voice_video_call_enabled?: boolean | null;
  is_fan_asks_how_model_looks_now_enabled?: boolean | null;
  is_fan_asks_for_verification_photo_enabled?: boolean | null;
  is_fan_asks_to_see_body_part_enabled?: boolean | null;
  is_fan_not_happy_enabled?: boolean | null;
  is_fan_suspects_ai_or_fake_enabled?: boolean | null;
  is_fan_no_money_enabled?: boolean | null;
  is_fan_asks_about_pricing_enabled?: boolean | null;
  is_fan_asks_off_platform_or_external_payment_enabled?: boolean | null;
  is_fan_offers_external_payment_enabled?: boolean | null;
  is_fan_sent_media_file_enabled?: boolean | null;
  is_fan_mentions_banned_platform_topic_enabled?: boolean | null;
  is_fan_mentions_other_model_page_enabled?: boolean | null;
  is_fan_stopped_responding_enabled?: boolean | null;
};

/** ADR 0055: per-folder request-mode / PPV override columns (null = inherit account policy). */
export type FolderAiModeOverrideFields = {
  content_automate_mode?: 'handoff' | 'automate' | null;
  custom_request_mode?: 'handoff' | 'automate' | 'decline' | null;
  call_request_mode?: 'handoff' | 'automate' | 'decline' | null;
  ppv_offer_active?: boolean | null;
};

export interface FolderAiSetup extends FolderAiInterruptOverrideFields, FolderAiModeOverrideFields {
  id: number;
  account_id: string;
  list_uuid: string;
  base_regime?: string | null;
  call_chatter_on_completion: boolean;
  hot_transition_mode?: 'horny' | 'moderate' | 'shy' | 'custom' | string | null;
  hot_transition_custom_prompt?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

// Mass Messages Types
export interface MassMessagesList {
  id: number;
  name: string;
  description: string | null;
  list_of_media_vaults: string[];
  content_probability?: number;
  emoji?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  message_count?: number;
}

/** A real Fansly audience list, distinct from mass-message text lists. */
export interface FanslyAudienceList {
  id: string;
  name: string;
  item_count?: number;
}

export type MessageTopic = 'life' | 'ass' | 'boobs' | 'feet' | 'pussy' | 'blowjob' | 'just_hot';

export const MESSAGE_TOPIC_OPTIONS: readonly MessageTopic[] = [
  'life',
  'ass',
  'boobs',
  'feet',
  'pussy',
  'blowjob',
  'just_hot',
] as const;

export interface MassMessage {
  id: number;
  message_text: string;
  description: string | null;
  status?: 'private' | 'public';
  topic?: MessageTopic | null;
  is_active: boolean;
  times_sent: number;
  total_recipients: number;
  response_count: number;
  response_rate: number | null;
  weight?: number;
}

/** Campaign touched when a message list was deleted (mass + priority). */
export interface ListDeleteCampaignRef { id: number; name: string }
export interface ListDeleteResult {
  message: string;
  /** Campaigns the list was removed from (weights renormalised). */
  campaigns_updated: ListDeleteCampaignRef[];
  /** Subset of the above left with no lists and therefore paused. */
  campaigns_deactivated: ListDeleteCampaignRef[];
}

export interface MassMessageCampaign {
  id: number;
  name: string;
  account_id: string;
  is_active: boolean;
  message_source: 'custom' | 'auto';
  lists_distribution: Record<string, number>;
  user_lists_to_include: string[];
  user_lists_to_exclude: string[];
  send_cadence_min_minutes: number;
  send_cadence_max_minutes: number;
  delete_after_min_minutes: number;
  delete_after_max_minutes: number;
  total_sends: number;
  total_responses: number;
  average_response_rate: number | null;
  last_executed_at: string | null;
  fansly_group_ids?: string[];
  fansly_include_list_ids?: string[];
  fansly_exclude_list_ids?: string[];
}

export interface MassMessageSchedule {
  id: number;
  account_id: string;
  campaign_id: number;
  message_id: number | null;
  message_text: string;
  content_id: string | null;
  scheduled_send_at: string;
  scheduled_delete_at: string;
  status: 'pending' | 'sending' | 'sent' | 'deleting' | 'deleted' | 'failed';
  sent_at: string | null;
  deleted_at: string | null;
  of_message_id: string | null;
  estimated_recipient_count: number;
  response_count: number;
}

export interface CampaignStatistics {
  campaign: MassMessageCampaign | PriorityMassMessageCampaign;
  total_schedules: number;
  by_status: Record<string, number>;
  average_recipients_per_send: number;
  response_time_stats: {
    avg_seconds: number;
    median_seconds: number;
    fastest_seconds: number;
  };
  top_performing_messages: Array<{
    message_text: string;
    sends: number;
    responses: number;
    response_rate: number;
  }>;
  timeline_data: Array<{
    date: string;
    sends: number;
    responses: number;
    response_rate: number;
  }>;
}

// Priority Mass Messages Types
export interface PriorityMassMessagesList {
  id: number;
  name: string;
  description: string | null;
  list_of_media_vaults: string[];
  content_probability?: number;
  emoji?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  message_count?: number;
}

export interface PriorityMassMessage {
  id: number;
  message_text: string;
  description: string | null;
  priority_level: number;
  status?: 'private' | 'public';
  topic?: MessageTopic | null;
  is_active: boolean;
  times_sent: number;
  total_individual_sends: number;
  response_count: number;
  response_rate: number | null;
  weight?: number;
}

export interface PriorityMassMessageCampaign {
  id: number;
  name: string;
  account_id: string;
  is_active: boolean;
  lists_distribution: Record<string, number>;
  user_lists_to_include: string[];
  user_lists_to_exclude: string[];
  send_cadence_min_minutes: number;
  send_cadence_max_minutes: number;
  delete_after_min_minutes: number;
  delete_after_max_minutes: number;
  min_priority_level: number;
  skip_deletion: boolean; // keep mode (ADR 0001): messages stay in DMs, never unsent
  total_sends: number;
  total_individual_sends: number;
  total_responses: number;
  average_response_rate: number | null;
  last_executed_at: string | null;
}

export interface PriorityMassMessageSchedule {
  id: number;
  account_id: string;
  campaign_id: number;
  priority_message_id: number | null;
  message_text: string;
  content_id: string | null;
  scheduled_send_at: string;
  scheduled_delete_at: string;
  status: string;
  send_started_at: string | null;
  send_completed_at: string | null;
  delete_started_at: string | null;
  delete_completed_at: string | null;
  target_user_ids: string[];
  total_target_users: number;
  successful_sends: number;
  failed_sends: number;
  successful_deletes: number;
  failed_deletes: number;
  response_count: number;
  cancel_state: 'requested' | 'processing' | 'completed' | null;
  cancel_requested_at: string | null;
  cancel_requested_by: string | null;
  cancel_started_at: string | null;
  cancel_completed_at: string | null;
  cancel_error: string | null;
}

// Automated Messages Types
export interface AutomatedMessagesList {
  list_id: number;
  list_name: string;
  description: string | null;
  owner_account_id: string;
  created_at: string;
  updated_at: string;
}

export interface AutomatedMessage {
  message_id: number;
  list_id: number;
  message_order: number;
  message_text: string;
  delay_seconds: number;
  created_at: string;
  /** of_content ids attached to this message; all share content_price. */
  content_ids: string[];
  /** Shared price for the attached content. null = sent free. */
  content_price: number | null;
}

export interface WelcomeStatsInterruptionReason {
  reason: string;
  count: number;
}

/** New-Subscription welcome-sequence funnel over a window (all-time if unbounded). */
export interface WelcomeStatsTotals {
  sequences_started: number;
  first_time_subs: number;
  resubscriptions: number;
  sequences_with_send: number;
  welcome_messages_sent: number;
  fans_replied: number;
  completed: number;
  interrupted: number;
  in_progress: number;
  failed: number;
  avg_messages_before_reply: number;
  interruption_reasons: WelcomeStatsInterruptionReason[];
}

export interface WelcomeStatsResponse {
  start: string | null;
  end: string | null;
  totals: WelcomeStatsTotals;
}

export interface OnlineOpenerStat {
  id: number;
  text: string;
  sent_count: number;
  converted_count: number;
}

/** Online-opener performance: all-time per-opener counters + a live snapshot. */
export interface OnlineStatsResponse {
  exists: boolean;
  enabled: boolean;
  message_source: 'auto' | 'custom';
  all_time_sent: number;
  all_time_converted: number;
  openers: OnlineOpenerStat[];
  live_hanging: number;
  live_cooldown: number;
  live_converted: number;
  live_failed: number;
  live_total: number;
}

export interface AccountAutomatedAssignment {
  account_id: string;
  list_id: number | null;
  is_enabled: boolean;
  assigned_at: string;
  list?: AutomatedMessagesList;
}

/** Online-message campaign config (ADR 0002). One per account. */
export interface OnlineMessageCampaign {
  id: number | null;
  account_id: string;
  is_enabled: boolean;
  send_period_seconds: number;
  cooldown_seconds: number;
  max_sends_per_iteration: number;
  include_lists: string[];
  exclude_lists: string[];
  add_online_to_list: boolean;
  online_list_name: string;
  /** 'auto' = use our recommended openers (default); 'custom' = operator's own pool. */
  message_source: 'auto' | 'custom';
}

/** One opener template in a campaign's pool, with conversion stats. */
export interface OnlineMessageTemplate {
  id: number;
  text: string;
  is_enabled: boolean;
  sent_count: number;
  converted_count: number;
}

export interface OnlineMessagesConfig {
  campaign: OnlineMessageCampaign;
  templates: OnlineMessageTemplate[];
  recommended_openers: string[];
}

export interface BannedWordList {
  words: string[];
  list_id: number | null;
  list_name: string;
}

/** Ban / not-preferable topic themes (censure), matches DB migration columns. */
export interface CensureTopicTheme {
  id: number;
  account_id: string;
  topic_heading: string;
  detection_prompt: string;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

// Copy operation types
export interface CopyResult {
  copied_lists: number;
  copied_messages: number;
  mappings: Record<string, any>;
}

export interface CopyCampaignResult {
  copied_campaigns: number;
  copied_lists: number;
  copied_messages: number;
  campaign_mappings: Record<number, number>;
}

export interface CopyModelContextResult {
  copied_fields: string[];
}

// Agency copy (cross-account settings copy)
export type AgencyCopyItemStatus = 'copied' | 'skipped' | 'forbidden' | 'error';

export interface AgencyCopyItemResult {
  key: string;
  status: AgencyCopyItemStatus;
  detail?: string | null;
}

export interface AgencyCopyTargetResult {
  account_id: string;
  items: AgencyCopyItemResult[];
}

export interface AgencyCopyResult {
  source_account_id: string;
  results: AgencyCopyTargetResult[];
}

// Agency entity (ADR 0017)
export type AgencyRole = 'owner' | 'admin' | 'member';

export interface Agency {
  id: string;
  name: string;
  /** Public URL of the agency picture; null = the initial is shown. */
  avatar_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** A fleet-picker row: an agency the caller co-manages (plain-member agencies are never listed). */
export interface AgencySummary extends Agency {
  role: AgencyRole | null; // null only for a super admin browsing an agency they are not in
  auto_placement: boolean;
  member_count: number;
  account_count: number;
}

export interface AgencyMemberRow {
  user_id: string;
  role: AgencyRole;
  created_at?: string;
}

export interface AgencyAccountRow {
  account_id: string;
  name: string;
  owner_id: string | null;
}

export interface AgencyDetail {
  agency: Agency;
  members: AgencyMemberRow[];
  account_ids: string[];
  accounts: AgencyAccountRow[];
}

/** An account the caller really owns, with the agency it is placed in (if any). */
export interface OwnedAccount {
  account_id: string;
  name: string;
  agency_id: string | null;
  agency_name: string | null;
}

/** An Account whose Team the caller runs through Permission Management. */
export interface ManagedAccount {
  account_id: string;
  name: string;
}

/** An agency fleet's Team row. `agency_role` null = External (access, no membership). */
export interface AgencyTeamRow {
  user_id: string;
  email: string | null;
  username: string | null;
  agency_role: AgencyRole | null;
  assigned_accounts_count: number;
}

// Interrupt Types
export interface Interrupt {
  id: number;
  name: string;
  description: string;
  created_at: string;
}

export interface InterruptAssignment {
  interrupt_id: number;
  account_id: string;
  status: boolean;
  count: number;
  last_triggered_at: string | null;
}

export interface InterruptStatus {
  id: number;
  name: string;
  description: string;
  created_at: string | null;
  is_assigned: boolean;
  status: boolean;
  count: number;
  last_triggered_at: string | null;
}

// ===== Sales ladder =====

export interface SalesAttachedVault {
  id: string;
  vault_name: string;
}

export interface SalesMediaItem {
  id: string;
  type: string | null;
  created_at: string | null;
  thumb_url: string | null;
  full_url: string | null;
  preview_url: string | null;
  width: number | null;
  height: number | null;
  duration: number | null;
  description: string | null;
  default_price: number | null;
  min_price: number | null;
  max_price: number | null;
  // Per-item Pinned price (ADR 0042): exact on-demand price + optional firm floor. null ⇒ not pinned.
  pinned_price?: number | null;
  pinned_floor?: number | null;
  sales_notes: string | null;
  sales_stage: string | null;
  // ADR 0031 — analysed status + per-item preview/exclusion flags, surfaced on the
  // media list so the adaptable content card can gate + render previews without a
  // per-item fetch. Optional: legacy cached items / non-sales responses may omit them.
  classified?: boolean;
  preview_content_ids?: string[];
  is_preview_content?: boolean;
  is_unused?: boolean;
  // ADR 0034 — OnlyFans creator user ids to tag as present in this media.
  tagged_user_ids?: number[];
}

export interface SalesMediaPage {
  list: SalesMediaItem[];
  has_more: boolean;
  next_offset: number;
}

export interface SalesStageSettingsEntry {
  description: string;
  price: number;
  min_price: number;
  // Optional deterministic content limits: how many photos/videos this stage's
  // PPV may contain. When set, bigdayz.new trims/back-fills the AI's picks to
  // stay within these ranges. Absent -> the AI decides freely.
  photos_min?: number;
  photos_max?: number;
  videos_min?: number;
  videos_max?: number;
  // Operator-authored Offer framing for this stage (ADR 0056) — how the AI words
  // the paid reveal. Sale stages only; absent -> the built-in code default.
  offer_framing?: string;
  // Explicit EXECUTION-order position (1-based) among all stages, teasing and
  // sale combined -- lets a teasing gift sit between two sale stages instead
  // of always clustering before them. sales_stages_settings is JSONB, which
  // does not preserve object key order, so this field (not dict order) is the
  // only reliable way to encode sequence. Absent on policies saved before this
  // field existed -- bigdayz.new falls back to the historical convention
  // (every teasing/$0 stage before every sale/priced stage) for those.
  order?: number;
}

export interface SalesPolicyBody {
  is_active: boolean;
  restrict_to_one_vault: boolean;
  sales_stages_settings?: Record<string, SalesStageSettingsEntry> | null;
  absolute_min_price: number | null;
  max_negotiation_attempts: number;
  on_max_negotiations: 'adjust_price_to_floor' | 'exit_to_communication' | 'notify' | 'continue';
  negotiation_grace_turns: number;
  max_negotiation_turns: number;
  negotiation_prompt: string | null;
  sales_strategy_prompt: string | null;
  notify_on_completion: boolean;
  notify_on_decline: boolean;
  continue_sales_on_decline: boolean;
  notify_when_fan_busy: boolean;
  notify_when_fan_disengaged: boolean;
}

export interface SalesPolicy extends SalesPolicyBody {
  exists: boolean;
  updated_at: string | null;
  applied_to_vault_ids: string[];
}

// ===== Content requests (on-demand content, ADR 0008/0009) =====

/** A `[default, floor]` price cell. `null` = unset (treated as free / $0). */
export type PricePair = [number | null, number | null];

/** The compositional pricing matrix (content_request_pricing.py shape). */
export interface ContentPricingRules {
  /** Photo unit price per nudity tier: non_nude | nude | explicit. */
  photo?: Record<string, PricePair>;
  /** Video unit price per `category -> minute-bucket` cell. */
  video?: Record<string, Record<string, PricePair>>;
  /** Bundle-size discount curve: `[count_threshold, multiplier]` entries. */
  bundle_discount?: Array<[number, number]>;
  /** Operator overrides pinning an exact price for a homogeneous archetype. */
  overrides?: ContentPriceOverride[];
  /** Count-shape fixed-price entries — the simplified "Standard bundles". Matched by the
   *  number of photos/videos in a runtime-composed bundle (mixed shapes included). Honored
   *  by bigdayz.new once its override matcher is extended; inert (ignored) until then. */
  shape_overrides?: ShapeContentOverride[];
  /** Inputs the simplified pricing screen authored the matrix from, round-tripped so the
   *  screen re-shows the operator's own numbers. Ignored by the runtime (unknown key). */
  simple?: SimplePricingInputs;
}

/** The three paid video price tiers (fixed, curated groupings of video categories). */
export type VideoTierKey = 'softcore' | 'explicit_solo' | 'hardcore';

/** A per-tier video price range: `[shortClipPrice, longClipPrice]`; length interpolates. */
export type TierRange = [number | null, number | null];

/** The simplified pricing screen's inputs (compiled down to {@link ContentPricingRules}). */
export interface SimplePricingInputs {
  /** Paid photo price (nude = explicit); non-nude stays free. */
  photo_price: number | null;
  /** Asking-price range per paid video tier. */
  video: Record<VideoTierKey, TierRange>;
  /** Negotiation floor as a single global percentage of the asking price (0–100). */
  floor_pct: number;
}

/** A Standard bundle: a fixed price for a content *shape* (counts of photos/videos). */
export interface ShapeContentOverride {
  shape: { photos: [number, number] | null; videos: [number, number] | null };
  /** `[asking, floor]` — the fixed price the assistant charges for a matching bundle. */
  price: PricePair;
  /** Stable id of the seeded default this row came from (blank for operator-added rows). */
  label?: string;
}

export interface ContentPriceOverride {
  media: 'photo' | 'video';
  tier?: string; // photo tier
  category?: string; // video category
  bucket?: string | null; // video minute-bucket (null = any)
  count: number;
  price: PricePair;
}

export interface ContentPolicyBody {
  is_active: boolean;
  automate_mode: 'handoff' | 'automate';
  pricing_rules: ContentPricingRules;
  no_content_handoff: boolean;
  max_negotiation_attempts: number;
  on_max_negotiations: 'ease_to_communication' | 'notify' | 'adjust_price_to_floor';
  strategy_prompt: string | null;
}

export interface ContentPolicy extends ContentPolicyBody {
  exists: boolean;
  updated_at: string | null;
  on_demand_vault_ids: string[];
}

export interface OnDemandVault {
  id: string;
  vault_name: string;
  total_items: number;
  classified_items: number;
  needs_review_items: number;
}

/** An operator-authored, named group of content sold together at a set price
 * (ADR 0041). `preview_ids` are fixed-mode previews (ADR 0031); `min_price`
 * NULL means the price is firm (no negotiation floor). */
export interface ContentBundle {
  id: string;
  account_id: string;
  name: string;
  description: string | null;
  content_ids: string[];
  preview_ids: string[] | null;
  price: number;
  min_price: number | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Create payload for a Content Bundle. PUT accepts the same shape partially.
 * `name`/`description`/`display_order` are optional — the UI no longer collects
 * them; the backend defaults the name (DB keeps it NOT NULL). */
export interface ContentBundleBody {
  name?: string;
  description?: string | null;
  content_ids: string[];
  preview_ids?: string[] | null;
  price: number;
  min_price?: number | null;
  display_order?: number;
  is_active?: boolean;
}

/** One operator-authored custom TYPE the account sells (custom_request_pricing.py).
 * The LLM SELECTS which entry a fan's ask matches; it never invents a number. */
export interface CustomPricingEntry {
  price: number;
  floor: number;
}

/** The flat `{ "<type description>": {price, floor} }` dict (ADR 0011) — NOT the
 * content_request compositional matrix. Empty = the branch can't quote (handoff). */
export type CustomPricings = Record<string, CustomPricingEntry>;

export interface CustomPolicyBody {
  is_active: boolean;
  custom_request_mode: 'handoff' | 'automate' | 'decline';
  custom_pricings: CustomPricings;
  photo_delivery_eta: string | null;
  video_delivery_eta: string | null;
  tip_tolerance: number;
  max_negotiation_attempts: number;
  strategy_prompt: string | null;
  /** Free-form pricing guidance handed to the LLM alongside custom_pricings. */
  pricing_guidance: string | null;
  /** Optional operator note steering how the AI warmly refuses in 'decline' mode. */
  decline_note?: string | null;
}

export interface CustomPolicy extends CustomPolicyBody {
  exists: boolean;
  updated_at: string | null;
}

export interface FanSentContentPolicyBody {
  is_active?: boolean;
  fan_sent_content_mode: 'ignore' | 'handoff' | 'analyse';
}

export interface FanSentContentPolicy extends FanSentContentPolicyBody {
  exists?: boolean;
  updated_at?: string | null;
}

export interface SpamPolicyBody {
  // Single combined switch: drives both the LLM spam judgement and the
  // verified-creator pre-filter on the backend.
  enabled: boolean;
  // OnlyFans list spam fans are moved into (blank => 'ignored_spam').
  spam_list_name: string;
}

export interface SpamPolicy extends SpamPolicyBody {
  exists?: boolean;
  updated_at?: string | null;
}

export interface CallPricingEntry {
  price: number;
  floor: number;
}

/** The flat `{ "<modality duration>": {price, floor} }` dict (ADR 0011/0014), keyed by
 * modality × duration (e.g. "video 30min"). Empty = the branch can't quote (handoff). */
export type CallPricings = Record<string, CallPricingEntry>;

export interface CallPolicyBody {
  is_active: boolean;
  call_request_mode: 'handoff' | 'automate' | 'decline';
  call_pricings: CallPricings;
  /** Free-text availability the AI relays verbatim — no calendar/slots in v1. */
  call_availability: string | null;
  /** Free-form pricing guidance handed to the LLM alongside call_pricings. */
  pricing_guidance: string | null;
  tip_tolerance: number;
  max_negotiation_attempts: number;
  strategy_prompt: string | null;
  /** Optional operator note steering how the AI warmly refuses in 'decline' mode. */
  decline_note?: string | null;
}

export interface CallPolicy extends CallPolicyBody {
  exists: boolean;
  updated_at: string | null;
}

export interface VaultAnalysisStatus {
  vault_id: string;
  /** idle | running | completed | error | no_credits | cancelled */
  status: string;
  running: boolean;
  tagged: number;
  total: number;
  /** True once a stop has been requested but the worker hasn't wound down yet. */
  cancel_requested?: boolean;
  error?: string | null;
  updated_at?: string | null;
}

export interface AnalysisStatusResponse {
  vaults: VaultAnalysisStatus[];
  remaining_generations: number;
}

/** One-shot payload backing the Attached-content tab's initial load. */
export interface ContentRequestsOverview {
  vaults: OnDemandVault[];
  all_vaults: OfVault[];
  sales_vault_ids: string[];
  statuses: VaultAnalysisStatus[];
  remaining_generations: number;
}

export interface VaultContentItem {
  id: string;
  type: string | null;
  /** Live OF CDN thumbnail (or signed storage URL for already-local content). */
  thumb_url: string | null;
  /** Live OF CDN full-media URL — carried so a video can be re-classified from the widget. */
  full_url: string | null;
  duration: number | null;
  description: string | null;
  classified: boolean;
  content_rating: number | null;
  exposure_level: number | null;
  scene_type: string | null;
  duration_bucket: string | null;
  classification: Record<string, unknown> | null;
  tag_source: string | null;
  tagged_at: string | null;
  needs_review: boolean | null;
  /** Dynamic per-item PPV previews — free media that rides into any bundle this
   * item is sold in (ADR 0031). */
  preview_content_ids: string[];
  /** Reserved as preview-only → excluded from all LLM selection (auto-set when
   * used as a preview; operator can toggle off). */
  is_preview_content: boolean;
  /** Operator retire switch → excluded from every LLM pool (fixed pinned packs
   * still send it). */
  is_unused: boolean;
  /** ADR 0034 — OnlyFans creator user ids tagged as present in this media;
   * attached to every send (free and paid) via the OF `rfTag` field. */
  tagged_user_ids: number[];
  /** Per-item Pinned price (ADR 0042): exact on-demand price + optional firm floor.
   * null ⇒ no pin (price from the grid). */
  pinned_price?: number | null;
  pinned_floor?: number | null;
  /** A single-item Analyse is in flight (durable across reloads; false until the
   * of_content analysis-status migration is applied). */
  analysing?: boolean;
  /** Why the last single-item Analyse failed, if it did. */
  analysis_error?: string | null;
}

export interface VaultContentPage {
  items: VaultContentItem[];
  total: number;
  has_more: boolean;
  next_offset: number;
}

/** A vault's item count + OF's cover thumbs, from one live vault-list read. */
export interface VaultCover {
  vault_id: string;
  total_items: number;
  photos: number;
  videos: number;
  medias: { type: string | null; thumb_url: string | null }[];
}

export interface VaultCounts {
  vault_id: string;
  total_items: number;
  classified_items: number;
  needs_review_items: number;
}

/** Operator edit of an item's PPV-preview list + exclusion flags (ADR 0031). */
export interface ContentFlagsBody {
  preview_content_ids: string[];
  is_preview_content: boolean;
  is_unused: boolean;
  /** ADR 0034 — OnlyFans creator user ids to tag as present in this media. */
  tagged_user_ids?: number[];
}

/** One eligible OnlyFans creator the account may tag in a Release-Form Tag (ADR 0034).
 * Populated live from OF's `tagged-friend-users` allow-list — the picker draws only
 * from this list because OF won't accept tags outside it. */
export interface TaggableUser {
  /** OF creator user id — what goes into rfTag on the send. */
  id: number;
  username: string;
  name?: string | null;
  avatar_url?: string | null;
  is_identity_verified?: boolean;
}

export interface TaggableUsersPage {
  items: TaggableUser[];
  has_more: boolean;
}

/** Operator hand-edit of one item's classification. Gates accept vocab tokens (e.g. 'nude'). */
export interface ClassificationEditBody {
  content_rating: string | null;
  exposure_level: string | null;
  scene_type: string | null;
  duration_bucket: string | null;
  focus_body: string[];
  present_body: string[];
  acts: string[];
  setting: string[];
  outfit: string[];
  vibe: string[];
  kink_tags: string[];
  partner_gender: string[];
  partner_count: number | null;
  face_visible: boolean | null;
  description: string | null;
}

export interface VaultClassifyResult {
  account_id: string;
  vault_id: string;
  processed: number;
  succeeded: number;
  results: Array<{ content_id: string; status: string; detail?: string | null }>;
  quota: { remaining_generations: number };
}

export interface BulkDescribeItem {
  content_id: string;
  thumb_url?: string | null;
  video_url?: string | null;
  type?: string | null;
  duration?: number | null;
}

export interface BulkDescribeResponse {
  described: number;
  skipped_already_described: number;
  skipped_no_thumb: number;
  failed: number;
  priced: number;
  remaining_generations: number;
  results: SalesMediaItem[];
}

export interface SalesItemUpdateBody {
  description?: string | null;
  default_price?: number | null;
  min_price?: number | null;
  max_price?: number | null;
  sales_notes?: string | null;
  sales_stage?: string | null;
}

// ===== Fixed sales ladders (ADR 0005) =====

/** One rung of a fixed ladder. Its sale format is derived engine-side from the
 * item count (bundle if >1 id else standalone) — never sent by the UI. Teasing rungs pin
 * price = min_price = 0. */
export interface FixedLadderStep {
  label: string;
  type: 'teasing' | 'sale';
  content_ids: string[];
  /** content_id -> owning vault_id, captured at selection time in the picker. */
  content_vaults: Record<string, string>;
  price: number;
  min_price: number;
  /** Operator-pinned free previews for this pack (ADR 0031) — injected free beside
   * the locked send when this step sells. */
  preview_ids: string[];
  /** Operator-authored Offer framing for this rung (ADR 0056) — how the AI words
   * the paid reveal. Sale rungs only; absent ⇒ the built-in code default. */
  offer_framing?: string;
}

export interface FixedLadderBody {
  priority: number;
  usage_notes: string | null;
  steps: FixedLadderStep[];
  is_active: boolean;
}

export interface FixedLadder extends FixedLadderBody {
  id: number;
  account_id: string;
  created_at: string | null;
  updated_at: string | null;
}

// ===== Tips policies =====

export interface HookStyle {
  heading: string;
  prompt: string;
}

export interface HotTipPolicyBody {
  is_active: boolean;
  hook_styles: HookStyle[];
  strategy_prompt: string | null;
  tip_min: number | null;
  allow_more: boolean;
  // Ask gap: arc turns the AI leaves the fan alone after he says no to an ask (ADR 0065).
  min_turns_between_asks: number;
  // Retired by ADR 0065 (no longer sent by the API): the placements alone decide how many
  // hot asks fire. Only the legacy Tips page and the no-points preset fallback read them.
  max_turns_between_asks?: number | null;
  hot_tip_cap?: number;
  overall_cap?: number;
  // Ask schedule — the regime is a preset that fills the points; points are saved for
  // every regime and are exactly what the engine runs (arc turns, hook = 1, so >= 2).
  placement_regime: PlacementRegime;
  arc_span_turns: number;
  ask_turn_points: number[] | null;
  // Per-ask pricing, indexed by ask ordinal (0 = 1st ask). tip_min is the floor.
  ask_prices: number[] | null;
  ask_disclose_prices: boolean[] | null;
  max_negotiation_attempts: number;
  negotiation_grace_turns: number;
  max_negotiation_turns: number;
  // Negotiation run-out: 'skip' = Keep sexting, 'exit' = Hand off. 'ease' is retired
  // (migrated to 'skip') and only read back from rows saved before ADR 0065.
  on_max_negotiations: 'skip' | 'ease' | 'exit';
  tip_drop_floor_pct: number;
  negotiation_prompt: string | null;
  notify_on_decline: boolean;
  notify_on_completion: boolean;
}

export type PlacementRegime =
  | 'beginning_only'
  | 'during'
  | 'beginning_during'
  | 'beginning_end'
  | 'during_end'
  | 'beginning_middle_end'
  | 'custom';

export interface HotTipPolicy extends HotTipPolicyBody {
  exists: boolean;
  id: number | null;
}

export interface NormalTipPolicyBody {
  is_active: boolean;
  chatting_trigger_turns: number;
  hook_styles: HookStyle[];
  nudge_strategy: string | null;
  tip_min: number | null;
  allow_more: boolean;
  disclose_amount: boolean;
  min_turns_between_asks: number;
  chatting_cap: number;
  overall_cap: number;
  max_negotiation_attempts: number;
  negotiation_grace_turns: number;
  max_negotiation_turns: number;
  on_max_negotiations: 'skip' | 'ease' | 'exit';
  tip_drop_floor_pct: number;
  negotiation_prompt: string | null;
  propose_conditions: string | null;
  notify_on_decline: boolean;
  notify_on_completion: boolean;
}

export interface NormalTipPolicy extends NormalTipPolicyBody {
  exists: boolean;
  id: number | null;
}

export interface VipOfferPolicyBody {
  is_active: boolean;
  page_url: string | null;
  page_price: number | null;
  trigger_turns: number;
  hook_styles: HookStyle[];
  offer_strategy: string | null;
  negotiation_prompt: string | null;
  propose_conditions: string | null;
  max_offers_per_session: number;
  max_offers_lifetime: number;
  min_turns_between_asks: number;
  combined_session_cap: number;
  min_turns_since_last_tip: number;
  negotiation_grace_turns: number;
  max_negotiation_attempts: number;
  max_negotiation_turns: number;
  on_max_negotiations: 'ease' | 'exit';
  block_after_decline: boolean;
  notify_on_decline: boolean;
  notify_on_completion: boolean;
}

export interface VipOfferPolicy extends VipOfferPolicyBody {
  exists: boolean;
  id: number | null;
}

// Subscription offer ("Paid plan offer" — ADR 0055). The Fansly analog of the VIP
// offer: invites a free follower to upgrade to the paid subscription ON THE SAME
// profile. Two differences from VIP: no page_url/page_price (the pitch lives in a
// free-text `offer_brief`, any price stated there in prose) and an optional list of
// pinned vault content ids sent FREE on the opening turn (photo-instructions).
export interface SubOfferPolicyBody {
  is_active: boolean;
  offer_brief: string | null;
  instruction_content_ids: string[];
  trigger_turns: number;
  hook_styles: HookStyle[];
  negotiation_prompt: string | null;
  propose_conditions: string | null;
  max_offers_per_session: number;
  max_offers_lifetime: number;
  min_turns_between_asks: number;
  combined_session_cap: number;
  min_turns_since_last_tip: number;
  negotiation_grace_turns: number;
  max_negotiation_attempts: number;
  max_negotiation_turns: number;
  on_max_negotiations: 'ease' | 'exit';
  block_after_decline: boolean;
  notify_on_decline: boolean;
  notify_on_completion: boolean;
}

export interface SubOfferPolicy extends SubOfferPolicyBody {
  exists: boolean;
  id: number | null;
}

// PPV offers — a proactive branch (ADR 0018) that judges the moment, opens a
// paid-content offer, negotiates and closes it. Carries its OWN ADR-0009
// pricing_rules, but by default (ADR 0032) prices from the account's content-request
// pricing instead (use_content_request_pricing) so one matrix is maintained. One
// active row per account; row existence activates the feature.
export type PpvProposeAggression = 'cautious' | 'balanced' | 'eager';
export type PpvOnMaxNegotiations = 'ease' | 'notify' | 'hold';

export interface PpvOfferPolicyBody {
  is_active: boolean;
  /** Own pricing rubric (same shape as content-request pricing). null → fail-safe band. */
  pricing_rules: ContentPricingRules | null;
  /** ADR 0032: when true (default), price from the account's content_policy.pricing_rules
   *  instead of `pricing_rules` above. bigdayz.new falls back own→fail-safe if content is blank. */
  use_content_request_pricing: boolean;
  // When to open an offer. propose_conditions (free text) overrides propose_aggression.
  propose_conditions: string | null;
  propose_aggression: PpvProposeAggression;
  theme_guidance: string | null;
  offer_strategy: string | null;
  // Two separate hook libraries — first-sell vs post-purchase upsell.
  sell_hook_styles: HookStyle[];
  upsell_hook_styles: HookStyle[];
  // Offer flow.
  tease_before_send: boolean;
  allow_upsell: boolean;
  max_upsell_chain: number | null; // null = unlimited (ride the hot buyer)
  handoff_on_agreement: boolean;
  // Deterministic gate (pacing).
  max_offers_per_session: number;
  min_turns_between_offers: number;
  cooldown_turns_after_decline: number;
  min_session_turns: number;
  lifetime_cap: number | null; // null / 0 = unlimited
  // Negotiation.
  negotiation_grace_turns: number;
  max_negotiation_attempts: number;
  max_negotiation_turns: number;
  on_max_negotiations: PpvOnMaxNegotiations;
}

export interface PpvOfferPolicy extends PpvOfferPolicyBody {
  exists: boolean;
  id: number | null;
}

// Paid sexting — a GATE mode of the hot_tip branch (ADR 0007). An active row
// takes precedence over any hot_tip policy. The AI sells but withholds all
// explicit sexting until the fan pays a gate tip (>= price_floor).
export type PaidSextingDeclineAction = 'back_off' | 'give_free' | 'handoff';

export interface PaidSextingPolicyBody {
  is_active: boolean;
  // Price gate.
  opening_price: number;
  price_floor: number; // > 0 and <= opening_price
  // Sell arc (reuses the hot hook-style library).
  hook_styles: HookStyle[];
  use_teasing_photo: boolean;
  warmup_min_turns: number;
  warmup_max_turns: number;
  // Negotiation (reuses hot_tip's sum-drop engine).
  negotiation_prompt: string | null;
  max_negotiation_attempts: number;
  negotiation_grace_turns: number;
  max_negotiation_turns: number;
  // Alerts.
  notify_on_decline: boolean;
  notify_on_completion: boolean;
  // Exit routing when the fan engages but never pays.
  decline_action: PaidSextingDeclineAction;
}

export interface PaidSextingPolicy extends PaidSextingPolicyBody {
  exists: boolean;
  id: number | null;
}

// ===== Content phase (unified life/teasing/sales) =====

export type ContentPhase = 'life' | 'teasing' | 'sales';

/** Sections of the unified Content tab. `paid` maps to of_vaults.is_paid_content. */
export type ContentLibrarySection = 'life' | 'teasing' | 'paid';

/** How eagerly the AI reaches for a spontaneous life photo (bigdayz.new life_content_policy). */
export type LifeAggression = 'reserved' | 'balanced' | 'eager';

/** Life-content settings body (Life section -> Settings tab). Mirrors life_content_policy
 *  plus the accounts.is_life_content_allowed master toggle. */
export interface LifeContentSettingsBody {
  is_life_content_allowed: boolean;
  max_sends_per_session: number;
  aggression: LifeAggression;
  detection_prompt: string | null;
}

export interface LifeContentSettings extends LifeContentSettingsBody {
  policy_exists: boolean;
  updated_at?: string | null;
}

/** Teasing-section settings — just the master on/off (accounts.is_teasing_content_allowed). */
export interface TeasingContentSettingsBody {
  is_teasing_content_allowed: boolean;
}

export interface ContentPhaseAttachedVault {
  id: string;
  vault_name: string;
}

export interface ContentItemUpdateBody {
  description?: string | null;
  sales_notes?: string | null;
  sales_stage?: string | null;
  // Per-item Pinned price (ADR 0042). Explicit null clears the pin.
  pinned_price?: number | null;
  pinned_floor?: number | null;
}

// Booster sessions (multi-login rate-limit relief). One descriptor per slot 1..3:
// slot 1 = Primary (owns the realtime socket), 2-3 = REST-only boosters.
export interface SessionSlot {
  slot: number;
  present: boolean;
  is_primary: boolean;
  proxy: 'shared' | 'own' | null;
  health: 'live' | 'dead' | null;
}

export interface AccountSessions {
  enabled: boolean;
  /** Whether launching a booster login is wired end-to-end yet. */
  booster_launch_enabled: boolean;
  primary: { is_logged_in: boolean };
  slots: SessionSlot[];
  can_add_booster: boolean;
  next_free_slot: number | null;
  /** Throttled = rate-limited into silence right now (every session cooling at
   *  once). Distinct from logged out; derived on read from all-cooling freshness. */
  throttled: boolean;
  last_all_cooling_at: string | null;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Opt-in hard ceiling (ms). When set, the request is aborted after this long and rejects
   *  with a clear timeout error — so a hung backend can't leave a spinner up forever. */
  timeoutMs?: number;
  /** Skip the GET dedup cache — for polled reads whose answer changes between calls. */
  noCache?: boolean;
}

/** Multipart file name for an avatar blob (the server sniffs the real type). */
function avatarFileName(image: Blob): string {
  return `avatar.${image.type.split('/')[1] || 'img'}`;
}

/** A non-2xx answer from the API. The message is the API's detail, as before; the
 *  status lets callers tell "logged out" (401) from "try again" (503). */
export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  /** Dedup cache for GET requests — same URL within TTL returns same promise */
  private _getCache = new Map<string, { promise: Promise<unknown>; ts: number }>();
  private static GET_CACHE_TTL = 3000; // 3 seconds
  /** Singleton promise for token refresh to serialize multiple 401s into one refresh call */
  private _refreshPromise: Promise<string | null> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Load token from localStorage on init
    this.token = localStorage.getItem('access_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  /** True while this browser holds a refresh token: the session can still be renewed. */
  hasSession(): boolean {
    return Boolean(localStorage.getItem('refresh_token'));
  }

  /**
   * Refresh the Supabase session once (concurrent callers share one request) and
   * return the new access token, or null. Used by the 401 retry below and by the
   * CRM frame, which runs on the same Supabase JWT and asks the console for a
   * fresh one instead of rotating the refresh token itself (ADR 0020).
   *
   * Only a refresh token the server rejects (401) ends the session. A failed
   * refresh — network down, server 503 — keeps both tokens, so the next request
   * refreshes again once the outage is over: an outage never logs anyone out.
   */
  async refreshAccessToken(): Promise<string | null> {
    if (!this._refreshPromise) {
      this._refreshPromise = this._refreshOnce().finally(() => {
        this._refreshPromise = null;
      });
    }
    return this._refreshPromise;
  }

  private async _refreshOnce(): Promise<string | null> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return null;
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      return null; // offline / unreachable: keep the session
    }
    if (response.status === 401) {
      this.setToken(null);
      localStorage.removeItem('refresh_token');
      return null;
    }
    if (!response.ok) return null; // 503 and friends: keep the session
    try {
      const refreshData = await response.json();
      if (!refreshData.access_token) return null;
      this.setToken(refreshData.access_token);
      if (refreshData.refresh_token) {
        localStorage.setItem('refresh_token', refreshData.refresh_token);
      }
      return refreshData.access_token;
    } catch {
      return null;
    }
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET' } = options;

    // Dedup identical GET requests within TTL window
    if (method === 'GET' && !options.noCache) {
      const now = Date.now();
      const cached = this._getCache.get(endpoint);
      if (cached && now - cached.ts < ApiClient.GET_CACHE_TTL) {
        return cached.promise as Promise<T>;
      }
      const promise = this._doRequest<T>(endpoint, options);
      this._getCache.set(endpoint, { promise: promise as Promise<unknown>, ts: now });
      // Cleanup on settle
      promise.catch(() => this._getCache.delete(endpoint));
      return promise;
    }

    // Non-GET requests: invalidate cache and execute directly
    this._getCache.clear();
    return this._doRequest<T>(endpoint, options);
  }

  /** Invalidate the GET dedup cache (call after mutations that change server state). */
  invalidateCache() {
    this._getCache.clear();
  }

  private async _doRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {}, timeoutMs } = options;

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    // Use in-memory token first, fallback to localStorage (e.g. after login before full re-render)
    const token = this.token ?? localStorage.getItem('access_token');
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Opt-in hard ceiling: abort the request after timeoutMs so a hung backend surfaces as a
    // clear error instead of an endless spinner. No timer ⇒ unchanged behavior for every caller.
    const controller = timeoutMs ? new AbortController() : undefined;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : undefined;

    try {
      let response = await fetch(`${this.baseUrl}${endpoint}`, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller?.signal,
      });

      // If 401 and we have a refresh token, refresh once and retry with the new token
      if (response.status === 401 && endpoint !== '/auth/refresh' && endpoint !== '/auth/login') {
        const freshToken = await this.refreshAccessToken();
        if (freshToken) {
          requestHeaders['Authorization'] = `Bearer ${freshToken}`;
          response = await fetch(`${this.baseUrl}${endpoint}`, {
            method,
            headers: requestHeaders,
            body: body ? JSON.stringify(body) : undefined,
            signal: controller?.signal,
          });
        }
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Request failed' }));
        const detail = error.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail) && detail.length
              ? detail.map((d: { msg?: string }) => d?.msg || String(d)).join(', ')
              : `HTTP error ${response.status}`;
        throw new ApiError(msg || `HTTP error ${response.status}`, response.status);
      }

      if (response.status === 204) return undefined as T;
      return response.json();
    } catch (err) {
      // A timeout abort becomes a clear, translatable-at-callsite error rather than the
      // opaque DOMException "The user aborted a request".
      if (controller?.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
        throw new Error('REQUEST_TIMEOUT');
      }
      throw err;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  // Auth endpoints
  async login(identifier: string, password: string) {
    const response = await this.request<{
      access_token: string;
      refresh_token: string;
      user: { id: string; email: string; role: string; username?: string; language?: string; avatar_url?: string | null; can_access_payments?: boolean; sidebar_account_order?: string[] };
    }>('/auth/login', {
      method: 'POST',
      body: { identifier, password },
    });
    this.setToken(response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    return response;
  }

  async signup(email: string, password: string, username: string | undefined, signupRole: SignupRole) {
    const response = await this.request<{
      access_token: string;
      refresh_token: string;
      user: { id: string; email: string; role: string; username?: string; language?: string; can_access_payments?: boolean; sidebar_account_order?: string[] };
    }>('/auth/signup', {
      method: 'POST',
      body: { email, password, username, signup_role: signupRole },
    });
    if (response.access_token) {
      this.setToken(response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
    }
    return response;
  }

  async logout() {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      this.setToken(null);
      localStorage.removeItem('refresh_token');
    }
  }

  async getMyTools() {
    return this.request<MyTools>('/auth/me/tools');
  }

  async getCurrentUser() {
    return this.request<{ id: string; email: string; role: string; username?: string; language?: string; styling?: Record<string, unknown> | null; avatar_url?: string | null; can_access_payments?: boolean; onboarding_completed_at?: string | null; telegram_required?: boolean; signup_role?: SignupRole | null; sidebar_account_order?: string[] }>('/auth/me');
  }

  // Telegram connection: a one-time t.me link, and the current state (polled while the User is in Telegram).
  async createTelegramLink() {
    return this.request<{ url: string; expires_at: string }>('/telegram/link', { method: 'POST' });
  }

  async getTelegramConnection() {
    return this.request<TelegramConnection>('/telegram/connection');
  }

  // Mark new-user onboarding as finished/skipped. Idempotent server-side.
  async completeOnboarding() {
    return this.request<{ id: string; email: string; role: string; username?: string; language?: string; can_access_payments?: boolean; onboarding_completed_at?: string | null; sidebar_account_order?: string[] }>('/auth/me/onboarding/complete', {
      method: 'POST',
    });
  }

  async updateMyProfile(data: { username?: string; language?: 'en' | 'uk' | 'ru' | 'es'; styling?: object; sidebar_account_order?: string[]; sidebar_layout?: SidebarLayout }) {
    return this.request<{ id: string; email: string; role: string; username?: string; language?: string; styling?: Record<string, unknown> | null; sidebar_account_order?: string[]; sidebar_layout?: SidebarLayout | null }>('/users/me', {
      method: 'PATCH',
      body: data,
    });
  }

  /** Set the signed-in user's profile picture; answers the updated profile. */
  async uploadMyAvatar(image: Blob) {
    const form = new FormData();
    form.append('file', image, avatarFileName(image));
    const r = await this.rawFetch('/users/me/avatar', { method: 'POST', body: form });
    return (await r.json()) as { avatar_url?: string | null };
  }

  async deleteMyAvatar() {
    return this.request<{ avatar_url?: string | null }>('/users/me/avatar', { method: 'DELETE' });
  }

  /** The person's Dashboard layout (ADR 0030); null until they first press Done. */
  async getDashboardLayout() {
    return this.request<DashboardLayoutDoc | null>('/users/me/dashboard-layout', { noCache: true });
  }

  /** Replace the person's Dashboard layout; the backend cleans it and returns what it kept. */
  async saveDashboardLayout(layout: DashboardLayoutDoc) {
    return this.request<DashboardLayoutDoc>('/users/me/dashboard-layout', { method: 'PUT', body: layout });
  }

  /** Attention items the person snoozed: { key: until ISO }. */
  async getDashboardDismissed() {
    return this.request<Record<string, string>>('/users/me/dashboard-dismissed', { noCache: true });
  }

  async saveDashboardDismissed(dismissed: Record<string, string>) {
    return this.request<Record<string, string>>('/users/me/dashboard-dismissed', { method: 'PUT', body: dismissed });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const response = await this.request<{
      message: string;
      access_token: string;
      refresh_token?: string;
    }>('/auth/change-password', {
      method: 'POST',
      body: {
        current_password: currentPassword,
        new_password: newPassword,
      },
    });
    this.setToken(response.access_token);
    if (response.refresh_token) {
      localStorage.setItem('refresh_token', response.refresh_token);
    }
    return response;
  }

  // Account endpoints
  async getAccounts() {
    return this.request<Account[]>('/accounts/');
  }

  /** OnlyFans' unread chats per live Account (ADR 0037); Accounts left out show no badge. */
  async getUnreadChats() {
    return this.request<Record<string, number>>('/accounts/unread-chats');
  }

  async getAccountsSubscriptionStatus() {
    return this.request<Record<string, AccountSubscriptionStatus>>('/accounts/subscription-status');
  }

  async getAccount(accountId: string) {
    return this.request<Account>(`/accounts/${accountId}`);
  }

  async setKillSwitch(accountId: string, on: boolean, reason?: string) {
    return this.request<Account>(`/accounts/${accountId}/kill-switch`, {
      method: 'POST',
      body: { on, reason: reason || null },
    });
  }

  async toggleAccount(accountId: string, isEnabled: boolean) {
    return this.request<Account>(`/accounts/${accountId}/toggle`, {
      method: 'POST',
      body: { is_enabled: isEnabled },
    });
  }

  async updateAccount(accountId: string, data: Partial<Account>) {
    return this.request<Account>(`/accounts/${accountId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async testTelegramNotification(
    accountId: string,
    opts: { telegramChatId?: string; notificationLanguage?: string } = {},
  ) {
    const body: Record<string, unknown> = {};
    if (opts.telegramChatId !== undefined) body.telegram_chat_id = opts.telegramChatId || null;
    if (opts.notificationLanguage !== undefined) body.notification_language = opts.notificationLanguage;
    return this.request<{
      ok: boolean;
      /** True when the group had been upgraded to a supergroup and we reconnected it. */
      migrated?: boolean;
      /** The chat id actually delivered to (updated when migrated). */
      telegram_chat_id?: string;
      telegram_health?: Account['telegram_health'];
      telegram_health_detail?: string | null;
    }>(`/accounts/${accountId}/test-telegram`, {
      method: 'POST',
      body,
    });
  }

  async createAccount(data: CreateAccountData) {
    return this.request<Account>('/accounts/', {
      method: 'POST',
      body: data,
    });
  }

  async deleteAccount(accountId: string) {
    return this.request(`/accounts/${accountId}`, { method: 'DELETE' });
  }

  async getAccountToken(accountId: string) {
    return this.request<{ token: string }>(`/accounts/${accountId}/token`);
  }

  // Rotate the connection key. Destructive: the old key stops working immediately.
  async regenerateAccountToken(accountId: string) {
    return this.request<{ token: string }>(`/accounts/${accountId}/regenerate-token`, {
      method: 'POST',
    });
  }

  // Poll target for the add-account "waiting" screen.
  async getAccountConnectionStatus(accountId: string) {
    return this.request<AccountConnectionStatus>(`/accounts/${accountId}/connection-status`);
  }

  // --- Streamed login (see ../../streamed-login-agent) ---

  // Pickable proxies + whether the account already has one + capability flag.
  async getProxyPool(accountId: string) {
    return this.request<ProxyPoolInfo>(`/accounts/${accountId}/proxy-pool`);
  }

  // Lock in the proxy for a FIRST connect, before opening the login stream.
  async setLoginProxy(accountId: string, body: SourceProxyRequest) {
    return this.request<{ ok: boolean; source: string }>(`/accounts/${accountId}/login-proxy`, {
      method: 'POST',
      body,
    });
  }

  // Re-source the proxy for an already-connected account; caller must then open
  // the login stream to force the fresh live login.
  async changeProxy(accountId: string, body: SourceProxyRequest) {
    return this.request<{ ok: boolean; source: string; relogin_required: boolean }>(
      `/accounts/${accountId}/change-proxy`,
      { method: 'POST', body },
    );
  }

  // Full ws(s):// URL for the login-stream relay, JWT carried as a query param
  // (browsers can't set an Authorization header on a WebSocket).
  loginStreamUrl(
    accountId: string,
    opts?: { role?: 'primary' | 'booster'; slot?: number },
  ): string {
    const token = this.getToken() ?? '';
    const httpBase = /^https?:\/\//.test(this.baseUrl)
      ? this.baseUrl
      : `${window.location.origin}${this.baseUrl}`;
    const u = new URL(`${httpBase}/accounts/${accountId}/login-stream`);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    u.searchParams.set('token', token);
    // Booster (multi-session) login rides the same relay; role/slot pass through
    // to the brain untouched. Omitted -> primary (slot 1), preserving old callers.
    if (opts?.role) u.searchParams.set('role', opts.role);
    if (opts?.slot != null) u.searchParams.set('slot', String(opts.slot));
    return u.toString();
  }

  async getAccountSessions(accountId: string): Promise<AccountSessions> {
    return this.request<AccountSessions>(`/accounts/${accountId}/sessions`);
  }

  /** Which of the user's accounts are Throttled right now (rate-limited into
   *  silence). Fetched once on the Dashboard for the card marker + "N throttled"
   *  tile — one batched read, distinct from logged-out. */
  async getThrottleStatus(): Promise<{ throttled_account_ids: string[] }> {
    return this.request<{ throttled_account_ids: string[] }>('/accounts/throttle-status');
  }

  async listSandboxChats(accountId: string) {
    return this.request<{ chats: SandboxChatSummary[] }>(
      `/sandbox/chats?account_id=${encodeURIComponent(accountId)}`,
      { noCache: true }
    );
  }

  async createSandboxChat(data: {
    account_id: string;
    profile: SandboxFanProfile;
    lists: string[];
    seed: SandboxSeedMessage[];
  }) {
    return this.request<{ account_id: string; fan_id: string; created_by: string }>('/sandbox/chats', {
      method: 'POST',
      body: data,
    });
  }

  /** The chat with transcript rows after `sinceId` (all when omitted). Polled, so never cached. */
  async getSandboxChat(accountId: string, fanId: string, sinceId?: number) {
    const q = sinceId !== undefined ? `?since_id=${sinceId}` : '';
    return this.request<SandboxChat>(`/sandbox/chats/${encodeURIComponent(accountId)}/${encodeURIComponent(fanId)}${q}`, {
      noCache: true,
    });
  }

  async sendSandboxFanMessage(accountId: string, fanId: string, text: string, pacing: SandboxPacing) {
    return this.request<{ message_id: number }>(this.sandboxChatPath(accountId, fanId, '/messages'), {
      method: 'POST',
      body: { text, pacing },
    });
  }

  async sendSandboxTip(accountId: string, fanId: string, amount: number, text = '') {
    return this.request<{ message_id: number }>(this.sandboxChatPath(accountId, fanId, '/tips'), {
      method: 'POST',
      body: { amount, text },
    });
  }

  async unlockSandboxPpv(accountId: string, fanId: string, messageId: number) {
    return this.request<{ message_id: number }>(this.sandboxChatPath(accountId, fanId, '/unlocks'), {
      method: 'POST',
      body: { message_id: messageId },
    });
  }

  async setSandboxLists(accountId: string, fanId: string, lists: string[]) {
    return this.request<{ lists: SandboxListRef[] }>(this.sandboxChatPath(accountId, fanId, '/lists'), {
      method: 'PUT',
      body: { lists },
    });
  }

  async deleteSandboxChat(accountId: string, fanId: string) {
    return this.request<void>(this.sandboxChatPath(accountId, fanId), { method: 'DELETE' });
  }

  /** One turn's traced LLM calls with full prompts. Super admins only. */
  async getSandboxTurnTrace(accountId: string, fanId: string, turnId: string) {
    return this.request<{ turn_id: string; calls: SandboxLlmCall[] }>(
      this.sandboxChatPath(accountId, fanId, `/turns/${encodeURIComponent(turnId)}`),
      { noCache: true }
    );
  }

  private sandboxChatPath(accountId: string, fanId: string, tail = '') {
    return `/sandbox/chats/${encodeURIComponent(accountId)}/${encodeURIComponent(fanId)}${tail}`;
  }

  async getBannedWords(accountId: string) {
    return this.request<BannedWordList>(`/accounts/${accountId}/banned-words`);
  }

  async updateBannedWords(accountId: string, words: string[]) {
    return this.request<BannedWordList>(`/accounts/${accountId}/banned-words`, {
      method: 'PUT',
      body: { words },
    });
  }

  async getCensureBanThemes(accountId: string) {
    return this.request<CensureTopicTheme[]>(`/accounts/${accountId}/censure/ban-themes`);
  }

  async getCensureNotPreferableThemes(accountId: string) {
    return this.request<CensureTopicTheme[]>(`/accounts/${accountId}/censure/not-preferable-themes`);
  }

  async createCensureBanTheme(
    accountId: string,
    body: { topic_heading: string; detection_prompt: string; is_active?: boolean }
  ) {
    return this.request<CensureTopicTheme>(`/accounts/${accountId}/censure/ban-themes`, {
      method: 'POST',
      body: { is_active: true, ...body },
    });
  }

  async createCensureNotPreferableTheme(
    accountId: string,
    body: { topic_heading: string; detection_prompt: string; is_active?: boolean }
  ) {
    return this.request<CensureTopicTheme>(`/accounts/${accountId}/censure/not-preferable-themes`, {
      method: 'POST',
      body: { is_active: true, ...body },
    });
  }

  async patchCensureBanTheme(
    accountId: string,
    themeId: number,
    body: { topic_heading?: string; detection_prompt?: string; is_active?: boolean }
  ) {
    return this.request<CensureTopicTheme>(`/accounts/${accountId}/censure/ban-themes/${themeId}`, {
      method: 'PATCH',
      body,
    });
  }

  async patchCensureNotPreferableTheme(
    accountId: string,
    themeId: number,
    body: { topic_heading?: string; detection_prompt?: string; is_active?: boolean }
  ) {
    return this.request<CensureTopicTheme>(`/accounts/${accountId}/censure/not-preferable-themes/${themeId}`, {
      method: 'PATCH',
      body,
    });
  }

  async deleteCensureBanTheme(accountId: string, themeId: number) {
    return this.request<{ ok: boolean }>(`/accounts/${accountId}/censure/ban-themes/${themeId}`, {
      method: 'DELETE',
    });
  }

  async deleteCensureNotPreferableTheme(accountId: string, themeId: number) {
    return this.request<{ ok: boolean }>(`/accounts/${accountId}/censure/not-preferable-themes/${themeId}`, {
      method: 'DELETE',
    });
  }

  async copyModelContext(accountId: string, sourceAccountId: string, fields: string[]) {
    return this.request<CopyModelContextResult>(`/accounts/${accountId}/copy-model-context`, {
      method: 'POST',
      body: { source_account_id: sourceAccountId, fields },
    });
  }

  // Agency: cross-account settings copy
  async copyAgencySettings(sourceAccountId: string, targetAccountIds: string[], items: string[]) {
    return this.request<AgencyCopyResult>('/agency/copy', {
      method: 'POST',
      body: {
        source_account_id: sourceAccountId,
        target_account_ids: targetAccountIds,
        items,
      },
    });
  }

  // Agency entity (ADR 0017): container grouping member Users + member Accounts
  /** Agencies the caller co-manages — the Team fleet picker. */
  async getMyAgencies() {
    return this.request<AgencySummary[]>('/agency');
  }

  /** Accounts the caller really owns, each with the agency it sits in. */
  async getMyOwnedAccounts() {
    return this.request<OwnedAccount[]>('/agency/my-accounts');
  }

  /** Accounts the caller runs Team for through Permission Management, not ownership. */
  async getMyManagedAccounts() {
    return this.request<ManagedAccount[]>('/users/me/team/managed-accounts');
  }

  async createAgency(name: string) {
    return this.request<Agency>('/agency', { method: 'POST', body: { name } });
  }

  async getAgencyDetail(agencyId: string) {
    return this.request<AgencyDetail>(`/agency/${agencyId}`);
  }

  async deleteAgency(agencyId: string) {
    return this.request<{ ok: boolean }>(`/agency/${agencyId}`, { method: 'DELETE' });
  }

  /** Set the agency picture (owner/admin); answers the updated agency. */
  async uploadAgencyAvatar(agencyId: string, image: Blob) {
    const form = new FormData();
    form.append('file', image, avatarFileName(image));
    const r = await this.rawFetch(`/agency/${agencyId}/avatar`, { method: 'POST', body: form });
    this._getCache.clear();
    return (await r.json()) as Agency;
  }

  async deleteAgencyAvatar(agencyId: string) {
    return this.request<Agency>(`/agency/${agencyId}/avatar`, { method: 'DELETE' });
  }

  /** Add a member by email or username; adding to an agency Team *is* membership. */
  async addAgencyMemberByIdentifier(agencyId: string, userIdentifier: string, role: AgencyRole) {
    return this.request<{ ok: boolean; user_id: string; email?: string; username?: string }>(
      `/agency/${agencyId}/members`,
      {
        method: 'POST',
        body: { user_identifier: userIdentifier, role },
      },
    );
  }

  async setAgencyMemberRole(agencyId: string, userId: string, role: AgencyRole) {
    return this.request<{ ok: boolean }>(`/agency/${agencyId}/members/${userId}`, {
      method: 'PATCH',
      body: { role },
    });
  }

  /** Remove: ends membership and revokes their granted access on this agency's accounts. */
  async removeAgencyMember(agencyId: string, userId: string) {
    return this.request<{ ok: boolean }>(`/agency/${agencyId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  async transferAgencyOwnership(agencyId: string, newOwnerId: string) {
    return this.request<{ ok: boolean }>(`/agency/${agencyId}/transfer`, {
      method: 'POST',
      body: { new_owner_id: newOwnerId },
    });
  }

  /** Account placement: add an account the caller really owns. */
  async placeAccountInAgency(agencyId: string, accountId: string) {
    return this.request<{ ok: boolean }>(`/agency/${agencyId}/accounts`, {
      method: 'POST',
      body: { account_id: accountId },
    });
  }

  async detachAccountFromAgency(accountId: string) {
    return this.request<{ ok: boolean }>(`/agency/accounts/${accountId}`, {
      method: 'DELETE',
    });
  }

  /** Auto-placement: accounts the caller creates go into this agency. */
  async setAgencyAutoPlacement(agencyId: string, enabled: boolean) {
    return this.request<{ ok: boolean }>(`/agency/${agencyId}/auto-placement`, {
      method: 'PUT',
      body: { enabled },
    });
  }

  async getAgencyTeam(agencyId: string) {
    return this.request<AgencyTeamRow[]>(`/agency/${agencyId}/team`);
  }

  /** A fleet's Team Roles: `fleet` null = the caller's Personal fleet. */
  async getTeamRoles(fleet: string | null) {
    return this.request<TeamRole[]>(fleet ? `/agency/${fleet}/team/roles` : '/users/me/team/roles');
  }

  async createTeamRole(fleet: string | null, role: TeamRoleInput) {
    return this.request<TeamRole>(fleet ? `/agency/${fleet}/team/roles` : '/users/me/team/roles', {
      method: 'POST',
      body: role,
    });
  }

  async updateTeamRole(fleet: string | null, roleId: string, role: TeamRoleInput) {
    const base = fleet ? `/agency/${fleet}/team/roles` : '/users/me/team/roles';
    return this.request<TeamRole & { sync: TeamRoleSync }>(`${base}/${roleId}`, { method: 'PUT', body: role });
  }

  /** Bring everyone linked to a Role back in line with it (after a partly failed save). */
  async reapplyTeamRole(fleet: string | null, roleId: string) {
    const base = fleet ? `/agency/${fleet}/team/roles` : '/users/me/team/roles';
    return this.request<TeamRoleSync>(`${base}/${roleId}/reapply`, { method: 'POST' });
  }

  async deleteTeamRole(fleet: string | null, roleId: string) {
    const base = fleet ? `/agency/${fleet}/team/roles` : '/users/me/team/roles';
    return this.request<{ ok: boolean }>(`${base}/${roleId}`, { method: 'DELETE' });
  }

  async getAgencyMemberAccounts(agencyId: string, userId: string) {
    return this.request<AccountAssignment[]>(`/agency/${agencyId}/team/${userId}/accounts`);
  }

  /** Everyone's grants in a fleet at once (null = Personal): user id -> assignments. */
  async getFleetAssignments(fleet: string | null) {
    return this.request<Record<string, AccountAssignment[]>>(fleet ? `/agency/${fleet}/team/accounts` : '/users/me/team/accounts');
  }

  async setAgencyMemberAccess(
    agencyId: string,
    userId: string,
    accountId: string,
    permissions: Partial<AccountPermissions>,
    crm?: CrmAccess,
    roleId?: string | null,
  ) {
    return this.request<{ ok: boolean }>(`/agency/${agencyId}/team/${userId}/accounts/${accountId}`, {
      method: 'PUT',
      body: { permissions, crm, role_id: roleId ?? null },
    });
  }

  async revokeAgencyMemberAccess(agencyId: string, userId: string, accountId: string) {
    return this.request<{ ok: boolean }>(`/agency/${agencyId}/team/${userId}/accounts/${accountId}`, {
      method: 'DELETE',
    });
  }

  // Model endpoints
  async getModels() {
    return this.request<Model[]>('/models/');
  }

  async getModel(modelId: string) {
    return this.request<Model>(`/models/${modelId}`);
  }

  async getModelAccounts(modelId: string) {
    return this.request<Account[]>(`/models/${modelId}/accounts`);
  }

  async createModel(data: { model_id: string; model_name: string }) {
    return this.request<Model>('/models/', {
      method: 'POST',
      body: data,
    });
  }

  async updateModel(modelId: string, modelName: string) {
    return this.request<Model>(`/models/${modelId}`, {
      method: 'PATCH',
      body: { model_name: modelName },
    });
  }

  async deleteModel(modelId: string) {
    return this.request(`/models/${modelId}`, { method: 'DELETE' });
  }

  // User management endpoints (owner only)
  async getUsers() {
    return this.request<User[]>('/users/');
  }

  async getUser(userId: string) {
    return this.request<UserWithAccess>(`/users/${userId}`);
  }

  async updateUserRole(userId: string, role: string) {
    return this.request<User>(`/users/${userId}/role`, {
      method: 'PATCH',
      body: { role },
    });
  }

  async deleteUser(userId: string) {
    return this.request(`/users/${userId}`, { method: 'DELETE' });
  }

  async getUserModelAccess(userId: string) {
    return this.request<string[]>(`/users/${userId}/access`);
  }

  async grantModelAccess(userId: string, modelId: string) {
    return this.request(`/users/${userId}/access`, {
      method: 'POST',
      body: { model_id: modelId },
    });
  }

  async revokeModelAccess(userId: string, modelId: string) {
    return this.request(`/users/${userId}/access/${modelId}`, { method: 'DELETE' });
  }

  // Account access methods (for account-based access control)
  async getUserAccountAccess(userId: string) {
    return this.request<string[]>(`/users/${userId}/access`);
  }

  async grantAccountAccess(userId: string, accountId: string) {
    return this.request(`/users/${userId}/access`, {
      method: 'POST',
      body: { account_id: accountId },
    });
  }

  async revokeAccountAccess(userId: string, accountId: string) {
    return this.request(`/users/${userId}/access/${accountId}`, { method: 'DELETE' });
  }

  // User limits management (super admin only)
  async getUserLimits(userId: string) {
    return this.request<UserLimits>(`/users/${userId}/limits`);
  }

  async setUserLimits(userId: string, limits: UserLimits) {
    return this.request(`/users/${userId}/limits`, {
      method: 'PUT',
      body: limits,
    });
  }

  // Account permissions management (super admin only)
  async revokeAccountPermission(userId: string, accountId: string) {
    return this.request(`/users/${userId}/permissions/${accountId}`, { method: 'DELETE' });
  }

  // API key management (current user)
  async getMyApiKeyInfo() {
    return this.request<ApiKeyInfo>('/users/me/api-key');
  }

  async generateMyApiKey() {
    return this.request<GenerateApiKeyResponse>('/users/me/api-key/generate', {
      method: 'POST',
    });
  }

  async revokeMyApiKey() {
    return this.request('/users/me/api-key', { method: 'DELETE' });
  }

  // Public API keys (Settings → API keys)
  async listPublicApiKeys() {
    return this.request<PublicApiKey[]>('/public-api-keys');
  }

  async listPublicApiScopes() {
    return this.request<PublicApiScope[]>('/public-api-keys/scopes');
  }

  async createPublicApiKey(body: { name: string; scopes: string[]; expires_at?: string | null }) {
    return this.request<CreatedPublicApiKey>('/public-api-keys', {
      method: 'POST',
      body,
    });
  }

  async revokePublicApiKey(keyId: string) {
    return this.request(`/public-api-keys/${keyId}`, { method: 'DELETE' });
  }

  // API key management (super admin)
  async getUserApiKeyInfo(userId: string) {
    return this.request<ApiKeyInfo>(`/users/${userId}/api-key`);
  }

  async revokeUserApiKey(userId: string) {
    return this.request(`/users/${userId}/api-key`, { method: 'DELETE' });
  }

  // Vault & content extraction endpoints
  async getVaultsForAccount(accountId: string) {
    return this.request<OfVault[]>(`/accounts/${accountId}/vaults`);
  }

  async getVaultContentPhase(accountId: string, phaseTag: AssistantPhaseTag) {
    return this.request<VaultContentPhase[]>(
      `/accounts/${accountId}/vault-content-phase?phase_tag=${encodeURIComponent(phaseTag)}`
    );
  }

  async addVaultContentPhase(accountId: string, vaultId: string, phaseTag: AssistantPhaseTag) {
    return this.request<VaultContentPhase>(`/accounts/${accountId}/vault-content-phase`, {
      method: 'POST',
      body: { vault_id: vaultId, phase_tag: phaseTag },
    });
  }

  async removeVaultContentPhase(accountId: string, vaultId: string, phaseTag: AssistantPhaseTag) {
    return this.request(
      `/accounts/${accountId}/vault-content-phase/${encodeURIComponent(vaultId)}?phase_tag=${encodeURIComponent(phaseTag)}`,
      { method: 'DELETE' }
    );
  }

  /**
   * List of_content for an account. Prefer phaseTag (joins content_content_phase).
   * Legacy: pass vaultIds only (no phaseTag).
   */
  async getOfContent(
    accountId: string,
    options?: { vaultIds?: string[]; phaseTag?: AssistantPhaseTag }
  ) {
    if (options?.phaseTag) {
      return this.request<OfContent[]>(
        `/accounts/${accountId}/of-content?phase_tag=${encodeURIComponent(options.phaseTag)}`
      );
    }
    const params = options?.vaultIds?.length
      ? `?vault_ids=${options.vaultIds.map(encodeURIComponent).join(',')}`
      : '';
    return this.request<OfContent[]>(`/accounts/${accountId}/of-content${params}`);
  }

  async updateOfContentDescription(contentId: string, description: string) {
    return this.patchOfContent(contentId, { description });
  }

  async patchOfContent(
    contentId: string,
    body: Partial<{
      description: string;
      default_price: number | null;
      min_price: number | null;
      max_price: number | null;
      usage_guidelines: string | null;
      // Per-item Pinned price (ADR 0042): exact on-demand price + optional firm floor.
      // Send null to clear a pin.
      pinned_price: number | null;
      pinned_floor: number | null;
    }>
  ) {
    return this.request<OfContent>(`/of-content/${encodeURIComponent(contentId)}`, {
      method: 'PATCH',
      body,
    });
  }

  async createContentExtractionRequest(
    accountId: string,
    vaultId: string,
    phaseTag: AssistantPhaseTag = 'teasing'
  ) {
    return this.request<ContentExtractionRequest>(`/accounts/${accountId}/content-extraction-request`, {
      method: 'POST',
      body: { vault_id: vaultId, phase_tag: phaseTag, content_phase: phaseTag },
    });
  }

  async getContentExtractionRequest(requestId: number) {
    return this.request<ContentExtractionRequest>(`/content-extraction-request/${requestId}`);
  }

  async deleteContentExtractionRequest(requestId: number) {
    return this.request<{ ok: boolean }>(`/content-extraction-request/${requestId}`, {
      method: 'DELETE',
    });
  }

  async deleteContentFromPhase(accountId: string, contentIds: string[], phaseTag: AssistantPhaseTag) {
    return this.request<{ ok: boolean }>(`/accounts/${accountId}/content-content-phase`, {
      method: 'DELETE',
      body: { content_ids: contentIds, phase_tag: phaseTag },
    });
  }

  /** Sync the account's vault collections from OF (synchronous) and return the fresh list. */
  async syncAccountVaults(accountId: string) {
    const res = await this.request<{ data: OfVault[] }>(`/accounts/${accountId}/sync/vaults`, {
      method: 'POST',
    });
    return res.data;
  }

  /** Sync the account's OF lists from OF (synchronous) and return the fresh assignments. */
  async syncAccountLists(accountId: string) {
    const res = await this.request<{ data: OfList[] }>(`/accounts/${accountId}/sync/lists`, {
      method: 'POST',
    });
    return res.data;
  }

  async createScheduleUpdateRequest(accountId: string, campaignId: number, isPriority: boolean) {
    const prefix = isPriority ? 'priority-' : '';
    return this.request<ScheduleUpdateRequest>(
      `/accounts/${accountId}/${prefix}mass-messages/campaigns/${campaignId}/regenerate-schedule`,
      { method: 'POST' },
    );
  }

  async getScheduleUpdateRequest(requestId: number) {
    return this.request<ScheduleUpdateRequest>(`/schedule-update-requests/${requestId}`);
  }

  /** Test endpoint: same as signed-thumb-url but returns parsed path and full error details. */
  async getTestSignedThumbUrl(thumbUrl: string) {
    return this.request<{
      ok: boolean;
      input_url?: string;
      parsed?: { bucket: string; object_path: string };
      signed_url?: string;
      error_type?: string;
      error?: string;
      message?: string;
      code?: string | number;
    }>(`/test-signed-thumb?thumb_url=${encodeURIComponent(thumbUrl)}`);
  }

  async getSignedThumbUrl(thumbUrl: string) {
    const urlPrefix = thumbUrl.slice(0, 80);
    console.log('[content-thumb] getSignedThumbUrl REQUEST', { urlPrefix });
    return this.request<{ signed_url: string }>(
      `/signed-thumb-url?thumb_url=${encodeURIComponent(thumbUrl)}`,
    ).then(
      (res) => {
        console.log('[content-thumb] getSignedThumbUrl SUCCESS', {
          urlPrefix,
          hasSignedUrl: !!res?.signed_url,
          signedPrefix: res?.signed_url?.slice(0, 60),
        });
        return res;
      },
      (err) => {
        console.log('[content-thumb] getSignedThumbUrl FAIL', { urlPrefix, err: String(err) });
        throw err;
      }
    );
  }

  /** Batch-sign many thumb URLs in one request. Returns a {raw: signed} map. */
  async getSignedThumbUrls(thumbUrls: string[]) {
    return this.request<{ signed: Record<string, string> }>(`/signed-thumb-urls`, {
      method: 'POST',
      body: { thumb_urls: thumbUrls },
    });
  }

  async getDescriptionGenerationQuota(accountId: string) {
    return this.request<DescriptionGenerationQuota>(`/accounts/${accountId}/description-generation-quota`);
  }

  async generateContentDescription(contentId: string) {
    return this.request<{ content: OfContent; quota: DescriptionGenerationQuota }>(
      `/of-content/${encodeURIComponent(contentId)}/generate-description`,
      { method: 'POST' }
    );
  }

  async getAccountOfLists(accountId: string) {
    return this.request<OfList[]>(`/accounts/${accountId}/of-lists`);
  }

  async getFolderAiSetups(accountId: string) {
    return this.request<FolderAiSetup[]>(`/accounts/${accountId}/folder-ai-setups`);
  }

  async upsertFolderAiSetup(
    accountId: string,
    data: {
      list_uuid: string;
      base_regime?: string | null;
      call_chatter_on_completion?: boolean;
      hot_transition_mode?: 'horny' | 'moderate' | 'shy' | 'custom' | string;
      hot_transition_custom_prompt?: string | null;
    } & Partial<FolderAiInterruptOverrideFields> & Partial<FolderAiModeOverrideFields>
  ) {
    return this.request<FolderAiSetup>(`/accounts/${accountId}/folder-ai-setups`, {
      method: 'POST',
      body: data,
    });
  }

  async deleteFolderAiSetup(accountId: string, listUuid: string) {
    return this.request<{ ok: boolean }>(`/accounts/${accountId}/folder-ai-setups/${encodeURIComponent(listUuid)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Refresh the account's OF lists. The sync is now a single synchronous request
   * (no queue / no polling). The timeout/poll params are unused and retained only
   * so existing callers don't need to change.
   */
  async refreshListsAndWait(accountId: string, _timeoutMs: number = 60_000, _pollMs: number = 3_000) {
    try {
      await this.syncAccountLists(accountId);
      return 'satisfied' as const;
    } catch {
      return 'failed' as const;
    }
  }

  // Mass Messages endpoints
  async getMassMessagesLists(accountId: string) {
    return this.request<MassMessagesList[]>(`/accounts/${accountId}/mass-messages/lists`);
  }

  async getFanslyAudienceLists(accountId: string) {
    return this.request<FanslyAudienceList[]>(`/accounts/${accountId}/fansly/lists`);
  }

  async getFanslyAudienceListItems(accountId: string, listId: string) {
    return this.request<Array<{ id?: string; userId?: string; accountId?: string }>>(
      `/accounts/${accountId}/fansly/lists/${encodeURIComponent(listId)}/items`
    );
  }

  async createMassMessagesList(accountId: string, data: { name: string; list_of_media_vaults?: string[]; content_probability?: number; emoji?: string | null }) {
    return this.request<MassMessagesList>(`/accounts/${accountId}/mass-messages/lists`, {
      method: 'POST',
      body: data,
    });
  }

  async updateMassMessagesList(accountId: string, listId: number, data: Partial<MassMessagesList>) {
    return this.request<MassMessagesList>(`/accounts/${accountId}/mass-messages/lists/${listId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteMassMessagesList(accountId: string, listId: number) {
    return this.request<ListDeleteResult>(`/accounts/${accountId}/mass-messages/lists/${listId}`, {
      method: 'DELETE',
    });
  }

  async getMassMessagesInList(accountId: string, listId: number) {
    return this.request<MassMessage[]>(`/accounts/${accountId}/mass-messages/lists/${listId}/messages`);
  }

  async addMassMessagesToList(accountId: string, listId: number, messagesText: string, description?: string) {
    return this.request<{ message: string; count: number; messages: MassMessage[] }>(
      `/accounts/${accountId}/mass-messages/lists/${listId}/messages`,
      {
        method: 'POST',
        body: { messages_text: messagesText, description },
      }
    );
  }

  async getRandomPublicMassMessageTexts(accountId: string, topic: MessageTopic, count: number) {
    const params = new URLSearchParams({ topic, count: count.toString() });
    return this.request<{ messages: string[] }>(
      `/accounts/${accountId}/mass-messages/random-public-texts?${params.toString()}`
    );
  }

  async generatePublicMassMessages(accountId: string, listId: number, count: number, topic: MessageTopic) {
    return this.request<{ message: string; count: number }>(
      `/accounts/${accountId}/mass-messages/lists/${listId}/generate-public`,
      {
        method: 'POST',
        body: { count, topic },
      }
    );
  }

  async removeMassMessageFromList(accountId: string, listId: number, messageId: number) {
    return this.request(`/accounts/${accountId}/mass-messages/lists/${listId}/messages/${messageId}`, {
      method: 'DELETE',
    });
  }

  async updateMassMessage(accountId: string, messageId: number, data: { message_text?: string; description?: string; is_active?: boolean }) {
    return this.request<MassMessage>(`/accounts/${accountId}/mass-messages/messages/${messageId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async getMassMessageCampaigns(accountId: string) {
    return this.request<MassMessageCampaign[]>(`/accounts/${accountId}/mass-messages/campaigns`);
  }

  async createMassMessageCampaign(accountId: string, data: {
    name: string;
    message_source?: 'custom' | 'auto';
    lists_distribution?: Record<string, number>;
    user_lists_to_include?: string[];
    user_lists_to_exclude?: string[];
    send_cadence_min_minutes?: number;
    send_cadence_max_minutes?: number;
    delete_after_min_minutes?: number;
    delete_after_max_minutes?: number;
    is_active?: boolean;
    fansly_group_ids?: string[];
    fansly_include_list_ids?: string[];
    fansly_exclude_list_ids?: string[];
  }) {
    return this.request<MassMessageCampaign>(`/accounts/${accountId}/mass-messages/campaigns`, {
      method: 'POST',
      body: data,
    });
  }

  async updateMassMessageCampaign(accountId: string, campaignId: number, data: Partial<MassMessageCampaign>) {
    return this.request<MassMessageCampaign>(`/accounts/${accountId}/mass-messages/campaigns/${campaignId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async toggleMassMessageCampaign(accountId: string, campaignId: number, isActive: boolean) {
    return this.request<MassMessageCampaign>(`/accounts/${accountId}/mass-messages/campaigns/${campaignId}/toggle`, {
      method: 'POST',
      body: { is_active: isActive },
    });
  }

  async deleteMassMessageCampaign(accountId: string, campaignId: number) {
    return this.request(`/accounts/${accountId}/mass-messages/campaigns/${campaignId}`, {
      method: 'DELETE',
    });
  }

  async getMassMessageCampaignSchedule(accountId: string, campaignId: number) {
    return this.request<MassMessageSchedule[]>(`/accounts/${accountId}/mass-messages/campaigns/${campaignId}/schedule`);
  }

  async updateMassMessageScheduleEntry(accountId: string, scheduleId: number, data: {
    message_text?: string;
    content_id?: string;
    scheduled_send_at?: string;
    scheduled_delete_at?: string;
  }) {
    return this.request<MassMessageSchedule>(`/accounts/${accountId}/mass-messages/schedule/${scheduleId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteMassMessageScheduleEntry(accountId: string, scheduleId: number) {
    return this.request(`/accounts/${accountId}/mass-messages/schedule/${scheduleId}`, {
      method: 'DELETE',
    });
  }

  async getMassMessageCampaignStatistics(accountId: string, campaignId: number) {
    return this.request<CampaignStatistics>(`/accounts/${accountId}/mass-messages/campaigns/${campaignId}/statistics`);
  }

  async copyMassMessagesLists(accountId: string, sourceAccountId: string, listIds: number[]) {
    return this.request<CopyResult>(`/accounts/${accountId}/mass-messages/copy`, {
      method: 'POST',
      body: { source_account_id: sourceAccountId, list_ids: listIds },
    });
  }

  async copyMassMessageCampaigns(accountId: string, sourceAccountId: string, campaignIds: number[]) {
    return this.request<CopyCampaignResult>(`/accounts/${accountId}/mass-messages/campaigns/copy`, {
      method: 'POST',
      body: { source_account_id: sourceAccountId, campaign_ids: campaignIds },
    });
  }

  // Priority Mass Messages endpoints
  async getPriorityMassMessagesLists(accountId: string) {
    return this.request<PriorityMassMessagesList[]>(`/accounts/${accountId}/priority-mass-messages/lists`);
  }

  async createPriorityMassMessagesList(accountId: string, data: { name: string; description?: string; list_of_media_vaults?: string[]; content_probability?: number; emoji?: string | null }) {
    return this.request<PriorityMassMessagesList>(`/accounts/${accountId}/priority-mass-messages/lists`, {
      method: 'POST',
      body: data,
    });
  }

  async updatePriorityMassMessagesList(accountId: string, listId: number, data: Partial<PriorityMassMessagesList>) {
    return this.request<PriorityMassMessagesList>(`/accounts/${accountId}/priority-mass-messages/lists/${listId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deletePriorityMassMessagesList(accountId: string, listId: number) {
    return this.request<ListDeleteResult>(`/accounts/${accountId}/priority-mass-messages/lists/${listId}`, {
      method: 'DELETE',
    });
  }

  async getPriorityMassMessagesInList(accountId: string, listId: number) {
    return this.request<PriorityMassMessage[]>(`/accounts/${accountId}/priority-mass-messages/lists/${listId}/messages`);
  }

  async addPriorityMassMessagesToList(accountId: string, listId: number, messagesText: string, description?: string, priorityLevel?: number) {
    return this.request<{ message: string; count: number; messages: PriorityMassMessage[] }>(
      `/accounts/${accountId}/priority-mass-messages/lists/${listId}/messages`,
      {
        method: 'POST',
        body: { messages_text: messagesText, description, priority_level: priorityLevel },
      }
    );
  }

  async getRandomPublicPriorityMassMessageTexts(accountId: string, topic: MessageTopic, count: number) {
    const params = new URLSearchParams({ topic, count: count.toString() });
    return this.request<{ messages: string[] }>(
      `/accounts/${accountId}/priority-mass-messages/random-public-texts?${params.toString()}`
    );
  }

  async generatePublicPriorityMassMessages(accountId: string, listId: number, count: number, topic: MessageTopic) {
    return this.request<{ message: string; count: number }>(
      `/accounts/${accountId}/priority-mass-messages/lists/${listId}/generate-public`,
      {
        method: 'POST',
        body: { count, topic },
      }
    );
  }

  async removePriorityMassMessageFromList(accountId: string, listId: number, messageId: number) {
    return this.request(`/accounts/${accountId}/priority-mass-messages/lists/${listId}/messages/${messageId}`, {
      method: 'DELETE',
    });
  }

  async updatePriorityMassMessage(accountId: string, messageId: number, data: { message_text?: string; description?: string; priority_level?: number; is_active?: boolean }) {
    return this.request<PriorityMassMessage>(`/accounts/${accountId}/priority-mass-messages/messages/${messageId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async getPriorityMassMessageCampaigns(accountId: string) {
    return this.request<PriorityMassMessageCampaign[]>(`/accounts/${accountId}/priority-mass-messages/campaigns`);
  }

  async createPriorityMassMessageCampaign(accountId: string, data: {
    name: string;
    lists_distribution?: Record<string, number>;
    user_lists_to_include?: string[];
    user_lists_to_exclude?: string[];
    send_cadence_min_minutes?: number;
    send_cadence_max_minutes?: number;
    delete_after_min_minutes?: number;
    delete_after_max_minutes?: number;
    min_priority_level?: number;
    is_active?: boolean;
    skip_deletion?: boolean;
  }) {
    return this.request<PriorityMassMessageCampaign>(`/accounts/${accountId}/priority-mass-messages/campaigns`, {
      method: 'POST',
      body: data,
    });
  }

  async updatePriorityMassMessageCampaign(accountId: string, campaignId: number, data: Partial<PriorityMassMessageCampaign>) {
    return this.request<PriorityMassMessageCampaign>(`/accounts/${accountId}/priority-mass-messages/campaigns/${campaignId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async togglePriorityMassMessageCampaign(accountId: string, campaignId: number, isActive: boolean) {
    return this.request<PriorityMassMessageCampaign>(`/accounts/${accountId}/priority-mass-messages/campaigns/${campaignId}/toggle`, {
      method: 'POST',
      body: { is_active: isActive },
    });
  }

  async deletePriorityMassMessageCampaign(accountId: string, campaignId: number) {
    return this.request(`/accounts/${accountId}/priority-mass-messages/campaigns/${campaignId}`, {
      method: 'DELETE',
    });
  }

  async getPriorityMassMessageCampaignSchedule(accountId: string, campaignId: number) {
    return this.request<PriorityMassMessageSchedule[]>(`/accounts/${accountId}/priority-mass-messages/campaigns/${campaignId}/schedule`);
  }

  async updatePriorityMassMessageScheduleEntry(accountId: string, scheduleId: number, data: {
    message_text?: string;
    content_id?: string;
    scheduled_send_at?: string;
    scheduled_delete_at?: string;
  }) {
    return this.request<PriorityMassMessageSchedule>(`/accounts/${accountId}/priority-mass-messages/schedule/${scheduleId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deletePriorityMassMessageScheduleEntry(accountId: string, scheduleId: number) {
    return this.request(`/accounts/${accountId}/priority-mass-messages/schedule/${scheduleId}`, {
      method: 'DELETE',
    });
  }

  async cancelPriorityMassMessageScheduleEntry(accountId: string, scheduleId: number) {
    return this.request<PriorityMassMessageSchedule>(
      `/accounts/${accountId}/priority-mass-messages/schedule/${scheduleId}/cancel`,
      { method: 'POST' }
    );
  }

  async getPriorityMassMessageCampaignStatistics(accountId: string, campaignId: number) {
    return this.request<CampaignStatistics>(`/accounts/${accountId}/priority-mass-messages/campaigns/${campaignId}/statistics`);
  }

  async copyPriorityMessagesLists(accountId: string, sourceAccountId: string, listIds: number[]) {
    return this.request<CopyResult>(`/accounts/${accountId}/priority-mass-messages/copy`, {
      method: 'POST',
      body: { source_account_id: sourceAccountId, list_ids: listIds },
    });
  }

  async copyPriorityMassMessageCampaigns(accountId: string, sourceAccountId: string, campaignIds: number[]) {
    return this.request<CopyCampaignResult>(`/accounts/${accountId}/priority-mass-messages/campaigns/copy`, {
      method: 'POST',
      body: { source_account_id: sourceAccountId, campaign_ids: campaignIds },
    });
  }

  // Automated Messages endpoints
  async getAutomatedMessagesLists(accountId: string) {
    return this.request<AutomatedMessagesList[]>(`/accounts/${accountId}/automated-messages/lists`);
  }

  async createAutomatedMessagesList(accountId: string, data: { list_name: string; description?: string }) {
    return this.request<AutomatedMessagesList>(`/accounts/${accountId}/automated-messages/lists`, {
      method: 'POST',
      body: data,
    });
  }

  async updateAutomatedMessagesList(accountId: string, listId: number, data: { list_name?: string; description?: string }) {
    return this.request<AutomatedMessagesList>(`/accounts/${accountId}/automated-messages/lists/${listId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteAutomatedMessagesList(accountId: string, listId: number) {
    return this.request(`/accounts/${accountId}/automated-messages/lists/${listId}`, {
      method: 'DELETE',
    });
  }

  async getAutomatedMessages(accountId: string, listId: number) {
    return this.request<AutomatedMessage[]>(`/accounts/${accountId}/automated-messages/lists/${listId}/messages`);
  }

  async createAutomatedMessage(accountId: string, listId: number, data: { message_text: string; message_order: number; delay_seconds: number; content_ids?: string[]; content_price?: number | null }) {
    return this.request<AutomatedMessage>(`/accounts/${accountId}/automated-messages/lists/${listId}/messages`, {
      method: 'POST',
      body: data,
    });
  }

  async updateAutomatedMessage(accountId: string, messageId: number, data: { message_text?: string; message_order?: number; delay_seconds?: number; content_ids?: string[]; content_price?: number | null }) {
    return this.request<AutomatedMessage>(`/accounts/${accountId}/automated-messages/messages/${messageId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteAutomatedMessage(accountId: string, messageId: number) {
    return this.request(`/accounts/${accountId}/automated-messages/messages/${messageId}`, {
      method: 'DELETE',
    });
  }

  async reorderAutomatedMessages(accountId: string, listId: number, messageOrders: Array<{ message_id: number; message_order: number }>) {
    return this.request(`/accounts/${accountId}/automated-messages/lists/${listId}/messages/reorder`, {
      method: 'POST',
      body: { message_orders: messageOrders },
    });
  }

  /** Welcome-sequence stats. Omit start/end for all-time; else pass ISO-8601 instants. */
  async getSubscriptionWelcomeStats(accountId: string, start?: string, end?: string) {
    const qs = new URLSearchParams();
    if (start) qs.set('start', start);
    if (end) qs.set('end', end);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request<WelcomeStatsResponse>(`/accounts/${accountId}/automated-messages/subscription-stats${suffix}`);
  }

  /** Online-opener performance: all-time per-opener counters + live snapshot. */
  async getOnlineMessagesStats(accountId: string) {
    return this.request<OnlineStatsResponse>(`/accounts/${accountId}/online-messages/stats`);
  }

  async getAccountAutomatedAssignment(accountId: string) {
    return this.request<AccountAutomatedAssignment | null>(`/accounts/${accountId}/automated-messages/assignment`);
  }

  async assignAutomatedMessagesToAccount(accountId: string, data: { list_id: number; is_enabled?: boolean }) {
    return this.request<AccountAutomatedAssignment>(`/accounts/${accountId}/automated-messages/assignment`, {
      method: 'POST',
      body: data,
    });
  }

  // ===== Online-message campaigns (ADR 0002) =====

  async getOnlineMessages(accountId: string) {
    return this.request<OnlineMessagesConfig>(`/accounts/${accountId}/online-messages`);
  }

  async updateOnlineMessagesCampaign(
    accountId: string,
    data: Partial<Pick<OnlineMessageCampaign, 'is_enabled' | 'send_period_seconds' | 'cooldown_seconds' | 'max_sends_per_iteration' | 'include_lists' | 'exclude_lists' | 'add_online_to_list' | 'online_list_name' | 'message_source'>>,
  ) {
    return this.request<OnlineMessageCampaign>(`/accounts/${accountId}/online-messages`, {
      method: 'PUT',
      body: data,
    });
  }

  async createOnlineMessageTemplate(accountId: string, data: { text: string; is_enabled?: boolean }) {
    return this.request<OnlineMessageTemplate>(`/accounts/${accountId}/online-messages/templates`, {
      method: 'POST',
      body: data,
    });
  }

  async bulkCreateOnlineMessageTemplates(accountId: string, texts: string[]) {
    return this.request<OnlineMessageTemplate[]>(`/accounts/${accountId}/online-messages/templates/bulk`, {
      method: 'POST',
      body: { texts },
    });
  }

  async updateOnlineMessageTemplate(accountId: string, templateId: number, data: { text?: string; is_enabled?: boolean }) {
    return this.request<OnlineMessageTemplate>(`/accounts/${accountId}/online-messages/templates/${templateId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async deleteOnlineMessageTemplate(accountId: string, templateId: number) {
    return this.request(`/accounts/${accountId}/online-messages/templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  async updateAccountAutomatedAssignment(accountId: string, data: { list_id?: number; is_enabled?: boolean }) {
    return this.request<AccountAutomatedAssignment>(`/accounts/${accountId}/automated-messages/assignment`, {
      method: 'PATCH',
      body: data,
    });
  }

  async removeAccountAutomatedAssignment(accountId: string) {
    return this.request(`/accounts/${accountId}/automated-messages/assignment`, {
      method: 'DELETE',
    });
  }

  async copyAutomatedMessagesLists(accountId: string, sourceAccountId: string, listIds: number[]) {
    return this.request<CopyResult>(`/accounts/${accountId}/automated-messages/copy`, {
      method: 'POST',
      body: { source_account_id: sourceAccountId, list_ids: listIds },
    });
  }

  // ============ Interrupts ============

  async getAccountInterrupts(accountId: string) {
    return this.request<InterruptStatus[]>(`/accounts/${accountId}/interrupts`);
  }

  async updateInterruptStatus(accountId: string, interruptId: number, status: boolean) {
    return this.request<InterruptStatus>(`/accounts/${accountId}/interrupts/${interruptId}`, {
      method: 'PATCH',
      body: { status },
    });
  }

  // ============ Team Management ============

  async getAccountPermissions(accountId: string) {
    return this.request<AccountPermissions>(`/accounts/${accountId}/permissions`);
  }

  // ============ Global Team Management (New System) ============

  async getGlobalTeam() {
    return this.request<GlobalTeamMember[]>('/users/me/team');
  }

  async addGlobalTeamMember(userIdentifier: string) {
    return this.request<{ message: string; user_id: string; email: string; username?: string }>('/users/me/team', {
      method: 'POST',
      body: { user_identifier: userIdentifier },
    });
  }

  async removeGlobalTeamMember(memberId: string) {
    return this.request(`/users/me/team/${memberId}`, {
      method: 'DELETE',
    });
  }

  /** Everyone with access to one of my accounts. */
  async getAccountMembers(accountId: string) {
    return this.request<AccountMember[]>(`/users/me/team/by-account/${accountId}`);
  }

  async getMemberAccountAssignments(memberId: string) {
    return this.request<AccountAssignment[]>(`/users/me/team/${memberId}/accounts`);
  }

  /** `roleId`: the Role picked for these grants, to link the person to it. */
  async assignMemberToAccount(memberId: string, accountId: string, permissions: AccountPermissions, crm?: CrmAccess, roleId?: string | null) {
    return this.request(`/users/me/team/${memberId}/accounts/${accountId}`, {
      method: 'POST',
      body: { permissions, crm, role_id: roleId ?? null },
    });
  }

  async updateMemberAccountPermissions(memberId: string, accountId: string, permissions: AccountPermissions, crm?: CrmAccess, roleId?: string | null) {
    return this.request(`/users/me/team/${memberId}/accounts/${accountId}`, {
      method: 'PATCH',
      body: { permissions, crm, role_id: roleId ?? null },
    });
  }

  async revokeMemberAccountAccess(memberId: string, accountId: string) {
    return this.request(`/users/me/team/${memberId}/accounts/${accountId}`, {
      method: 'DELETE',
    });
  }

  // ============ Dashboard Statistics ============

  async getDashboardStatistics() {
    return this.request<DashboardStatistics>('/users/me/statistics');
  }

  // ============ Payments ============

  async getPaymentsSummary() {
    return this.request<PaymentsSummary>('/payments/summary');
  }

  async createTopUpInvoice(
    amountCents: number,
    currency: 'USDT' | 'USDC',
    options?: { sandbox_case?: string; code?: string }
  ) {
    const body: Record<string, unknown> = { amount_cents: amountCents, currency };
    const c = options?.sandbox_case?.trim();
    if (c) body.sandbox_case = c;
    const referral = options?.code?.trim();
    if (referral) body.code = referral;
    return this.request<TopUpInvoice>('/payments/top-up/invoice', {
      method: 'POST',
      body,
    });
  }

  async previewTopUpReferral(amountCents: number, code: string) {
    return this.request<{ ok: boolean; is_referral: boolean; bonus_cents: number; reason?: string }>(
      '/payments/top-up/preview-referral',
      { method: 'POST', body: { amount_cents: amountCents, code } }
    );
  }

  async checkoutSubscriptions(data: {
    account_ids?: string[];
    pay_all?: boolean;
    promo_code?: string;
    idempotency_key?: string;
    selected_plan_code?: string;
  }) {
    return this.request<SubscriptionCheckoutResult>('/payments/subscriptions/checkout', {
      method: 'POST',
      body: data,
    });
  }

  async checkoutSubscriptionsV2(data: {
    items: Array<{ account_id: string; plan_code: string }>;
    promo_code?: string;
    idempotency_key?: string;
  }) {
    return this.request<SubscriptionCheckoutResult>('/payments/subscriptions/checkout-v2', {
      method: 'POST',
      body: data,
    });
  }

  async upgradeSubscriptionToAssistant(data: { account_id: string; idempotency_key?: string }) {
    return this.request<UpgradeAssistantResult>('/payments/subscriptions/upgrade-assistant', {
      method: 'POST',
      body: data,
    });
  }

  async updateSubscriptionAutoRenew(data: { account_id: string; enabled: boolean; plan_code?: string }) {
    return this.request<AutoRenewResult>('/payments/subscriptions/auto-renew', {
      method: 'PATCH',
      body: data,
    });
  }

  /** Switch an account's billing mode (ADR 0012). `metered`/`turn` are owner-facing;
   *  `unlimited` is admin-only. */
  async setBillingMode(data: { account_id: string; mode: 'metered' | 'turn' | 'unlimited' }) {
    return this.request<BillingModeResult>('/payments/billing-mode', {
      method: 'PUT',
      body: data,
    });
  }

  /** On-demand Monthly-balance extraction — the "Get price" button (ADR 0013).
   *  Fetches the account's earnings from its platform gateway (OnlyFans or Fansly)
   *  and returns the freshly tiered Subscription price. */
  async refreshAccountPrice(accountId: string) {
    return this.request<RefreshPriceResponse>(`/payments/accounts/${accountId}/refresh-price`, {
      method: 'POST',
      // Hard ceiling just above the backend's 90s gateway read timeout, so a genuine slow
      // fetch still lands but a hung request can't spin forever.
      timeoutMs: 100_000,
    });
  }

  /** Set the per-account metered Overage Cap: 'continue' (bill overage) or 'hard_stop'
   *  (suspend at the Included block until next period). */
  async setOverageCap(data: { account_id: string; overage_mode: 'continue' | 'hard_stop' }) {
    return this.request<{ account_id: string; overage_mode: string }>('/payments/cap', {
      method: 'PUT',
      body: data,
    });
  }

  /** Per-account metered usage: Included turns, used, overage (real + would-be), cap, rate. */
  async getMeteredUsage() {
    return this.request<MeteredUsage>('/payments/metered/usage');
  }

  /** Manually run the metered sweep now (super admin only). */
  async runMeteredSweep() {
    return this.request<Record<string, unknown>>('/payments/metered/run-sweep', { method: 'POST' });
  }

  /** Per-account on-demand (turn billing) usage: real amount charged, responses, pending.
   *  Optional ISO start/end restrict the charged figures to that period. */
  async getTurnBillingUsage(params?: { start?: string; end?: string }) {
    const q = new URLSearchParams();
    if (params?.start) q.set('start', params.start);
    if (params?.end) q.set('end', params.end);
    const qs = q.toString();
    return this.request<TurnBillingUsage>(`/payments/turn-billing/usage${qs ? `?${qs}` : ''}`);
  }

  /** Manually run the turn-billing sweep now (super admin only) — same logic as the
   *  30-min background job, so pending accrual is swept into a real charge on demand. */
  async runTurnBillingSweep() {
    return this.request<TurnBillingSweepResult>('/payments/turn-billing/run-sweep', {
      method: 'POST',
    });
  }

  async getNotificationSettings() {
    return this.request<SubscriptionNotificationSettings>('/payments/notification-settings');
  }

  async updateNotificationSettings(data: {
    subscription_chat_id: string | null;
    advance_days: number[];
  }) {
    return this.request<SubscriptionNotificationSettings>('/payments/notification-settings', {
      method: 'PUT',
      body: data,
    });
  }

  async testSubscriptionTelegram(chatId: string) {
    return this.request<{ ok: boolean }>('/payments/notification-settings/test', {
      method: 'POST',
      body: { chat_id: chatId },
    });
  }

  // ===== Sales ladder =====

  async listAttachedSalesVaults(accountId: string) {
    return this.request<SalesAttachedVault[]>(`/accounts/${accountId}/sales/vaults`);
  }

  async attachSalesVault(accountId: string, vaultId: string) {
    return this.request<{ ok: boolean }>(
      `/accounts/${accountId}/sales/vaults/${encodeURIComponent(vaultId)}/attach`,
      { method: 'POST' }
    );
  }

  async detachSalesVault(accountId: string, vaultId: string) {
    return this.request<{ ok: boolean }>(
      `/accounts/${accountId}/sales/vaults/${encodeURIComponent(vaultId)}/detach`,
      { method: 'POST' }
    );
  }

  async fetchSalesVaultMedia(
    accountId: string,
    vaultId: string,
    offset: number,
    limit: number
  ) {
    return this.request<SalesMediaPage>(
      `/accounts/${accountId}/sales/vaults/${encodeURIComponent(vaultId)}/media`,
      { method: 'POST', body: { offset, limit } }
    );
  }

  /** Resolve pinned content_ids to their owning vault from local data only —
   *  no live upstream call. Used to lazy-load just the vaults an existing
   *  fixed ladder's rungs actually reference, instead of every vault. */
  async resolveContentVaults(accountId: string, contentIds: string[]) {
    return this.request<{ vault_ids: Record<string, string> }>(
      `/accounts/${accountId}/sales/content/vaults`,
      { method: 'POST', body: { content_ids: contentIds } }
    );
  }

  async updateSalesItem(
    accountId: string,
    vaultId: string,
    contentId: string,
    body: SalesItemUpdateBody
  ) {
    return this.request<SalesMediaItem>(
      `/accounts/${accountId}/sales/vaults/${encodeURIComponent(vaultId)}/items/${encodeURIComponent(contentId)}`,
      { method: 'PUT', body }
    );
  }

  async generateSalesItemDescription(
    accountId: string,
    vaultId: string,
    contentId: string,
    body: { thumb_url?: string | null; type?: string | null }
  ) {
    return this.request<{
      description: string | null;
      sales_stage: string | null;
      default_price: number | null;
      min_price: number | null;
      max_price: number | null;
      remaining_generations: number;
    }>(
      `/accounts/${accountId}/sales/vaults/${encodeURIComponent(vaultId)}/items/${encodeURIComponent(contentId)}/generate-description`,
      { method: 'POST', body }
    );
  }

  async bulkDescribeVault(
    accountId: string,
    vaultId: string,
    body: { items: BulkDescribeItem[] }
  ) {
    return this.request<BulkDescribeResponse>(
      `/accounts/${accountId}/sales/vaults/${encodeURIComponent(vaultId)}/bulk-describe`,
      { method: 'POST', body }
    );
  }

  async bulkDescribeContentVault(
    accountId: string,
    vaultId: string,
    body: { phase: ContentPhase; items: BulkDescribeItem[] }
  ) {
    return this.request<BulkDescribeResponse>(
      `/accounts/${accountId}/content-phase/vaults/${encodeURIComponent(vaultId)}/bulk-describe`,
      { method: 'POST', body }
    );
  }

  async getSalesPolicy(accountId: string) {
    return this.request<SalesPolicy>(`/accounts/${accountId}/sales/policy`);
  }

  async putSalesPolicy(accountId: string, body: SalesPolicyBody) {
    return this.request<SalesPolicy>(`/accounts/${accountId}/sales/policy`, {
      method: 'PUT',
      body,
    });
  }

  async deleteSalesPolicy(accountId: string, force = false) {
    const qs = force ? '?force=true' : '';
    return this.request<{ ok: boolean; deleted: number }>(
      `/accounts/${accountId}/sales/policy${qs}`,
      { method: 'DELETE' }
    );
  }

  // ===== Content requests (on-demand content, ADR 0008/0009) =====

  /** Single-request initial load for the Attached-content tab: attached vaults (with counts),
   * every vault for the picker, sales-pack ids to hide, run statuses + quota. */
  async getContentRequestsOverview(accountId: string) {
    return this.request<ContentRequestsOverview>(
      `/accounts/${accountId}/content-requests/overview`
    );
  }

  async listOnDemandVaults(accountId: string) {
    return this.request<OnDemandVault[]>(`/accounts/${accountId}/content-requests/vaults`);
  }

  async attachOnDemandVault(accountId: string, vaultId: string) {
    return this.request<{ ok: boolean }>(
      `/accounts/${accountId}/content-requests/vaults/${encodeURIComponent(vaultId)}/attach`,
      { method: 'POST' }
    );
  }

  async detachOnDemandVault(accountId: string, vaultId: string) {
    return this.request<{ ok: boolean }>(
      `/accounts/${accountId}/content-requests/vaults/${encodeURIComponent(vaultId)}/detach`,
      { method: 'POST' }
    );
  }

  // ===== Unified Content tab (life | teasing | paid) — section-aware attach/detach.
  // Every attach also flips is_on_demand=true server-side; classification (analyse /
  // counts / status) is vault-scoped and reuses the content-requests methods above.
  async getContentLibraryOverview(accountId: string, section: ContentLibrarySection) {
    return this.request<ContentRequestsOverview>(
      `/accounts/${accountId}/content-library/${section}/overview`
    );
  }

  async listContentLibraryVaults(accountId: string, section: ContentLibrarySection) {
    return this.request<OnDemandVault[]>(
      `/accounts/${accountId}/content-library/${section}/vaults`
    );
  }

  async attachContentLibraryVault(
    accountId: string,
    section: ContentLibrarySection,
    vaultId: string
  ) {
    return this.request<{ ok: boolean; is_on_demand: boolean }>(
      `/accounts/${accountId}/content-library/${section}/vaults/${encodeURIComponent(vaultId)}/attach`,
      { method: 'POST' }
    );
  }

  async detachContentLibraryVault(
    accountId: string,
    section: ContentLibrarySection,
    vaultId: string
  ) {
    return this.request<{ ok: boolean; is_on_demand: boolean }>(
      `/accounts/${accountId}/content-library/${section}/vaults/${encodeURIComponent(vaultId)}/detach`,
      { method: 'POST' }
    );
  }

  async getLifeContentSettings(accountId: string) {
    return this.request<LifeContentSettings>(
      `/accounts/${accountId}/content-library/life/settings`
    );
  }

  async putLifeContentSettings(accountId: string, body: LifeContentSettingsBody) {
    return this.request<LifeContentSettings>(
      `/accounts/${accountId}/content-library/life/settings`,
      { method: 'PUT', body }
    );
  }

  async getTeasingContentSettings(accountId: string) {
    return this.request<TeasingContentSettingsBody>(
      `/accounts/${accountId}/content-library/teasing/settings`
    );
  }

  async putTeasingContentSettings(accountId: string, body: TeasingContentSettingsBody) {
    return this.request<TeasingContentSettingsBody>(
      `/accounts/${accountId}/content-library/teasing/settings`,
      { method: 'PUT', body }
    );
  }

  async getContentPolicy(accountId: string) {
    return this.request<ContentPolicy>(`/accounts/${accountId}/content-requests/policy`);
  }

  async putContentPolicy(accountId: string, body: ContentPolicyBody) {
    return this.request<ContentPolicy>(`/accounts/${accountId}/content-requests/policy`, {
      method: 'PUT',
      body,
    });
  }

  // ── Content Bundles (ADR 0041) — operator-authored, set-priced content groups ──
  async getContentBundles(accountId: string) {
    return this.request<{ bundles: ContentBundle[] }>(`/accounts/${accountId}/content-bundles`);
  }

  async createContentBundle(accountId: string, body: ContentBundleBody) {
    return this.request<{ bundle: ContentBundle }>(`/accounts/${accountId}/content-bundles`, {
      method: 'POST',
      body,
    });
  }

  async updateContentBundle(accountId: string, bundleId: string, body: Partial<ContentBundleBody>) {
    return this.request<{ bundle: ContentBundle }>(
      `/accounts/${accountId}/content-bundles/${encodeURIComponent(bundleId)}`,
      { method: 'PUT', body },
    );
  }

  async deleteContentBundle(accountId: string, bundleId: string) {
    return this.request<{ ok: true }>(
      `/accounts/${accountId}/content-bundles/${encodeURIComponent(bundleId)}`,
      { method: 'DELETE' },
    );
  }

  /** Whether the fully-automated sales ladder is built from Content Bundles (ADR 0041 §6). */
  async getUseBundles(accountId: string) {
    return this.request<{ use_bundles: boolean }>(`/accounts/${accountId}/auto-ladder/use-bundles`);
  }

  async setUseBundles(accountId: string, useBundles: boolean) {
    return this.request<{ use_bundles: boolean }>(`/accounts/${accountId}/auto-ladder/use-bundles`, {
      method: 'PUT',
      body: { use_bundles: useBundles },
    });
  }

  /** Full-automation ladder strategy: 'bundles' (composition photo+video ladder,
   *  the default) or 'video_only' (legacy per-clip engine). Persisted as the
   *  auto_ladder_override.engine key read by bigdayz.new. */
  async getAutoLadderStrategy(accountId: string) {
    return this.request<{ strategy: LadderStrategy }>(`/accounts/${accountId}/auto-ladder/strategy`);
  }

  async setAutoLadderStrategy(accountId: string, strategy: LadderStrategy) {
    return this.request<{ strategy: LadderStrategy }>(`/accounts/${accountId}/auto-ladder/strategy`, {
      method: 'PUT',
      body: { strategy },
    });
  }

  async getCustomPolicy(accountId: string) {
    return this.request<CustomPolicy>(`/accounts/${accountId}/custom-requests/policy`);
  }

  async putCustomPolicy(accountId: string, body: CustomPolicyBody) {
    return this.request<CustomPolicy>(`/accounts/${accountId}/custom-requests/policy`, {
      method: 'PUT',
      body,
    });
  }

  async getFanSentContentPolicy(accountId: string) {
    return this.request<FanSentContentPolicy>(`/accounts/${accountId}/fan-sent-content/policy`);
  }

  async putFanSentContentPolicy(accountId: string, body: FanSentContentPolicyBody) {
    return this.request<FanSentContentPolicy>(`/accounts/${accountId}/fan-sent-content/policy`, {
      method: 'PUT',
      body,
    });
  }

  async getSpamPolicy(accountId: string) {
    return this.request<SpamPolicy>(`/accounts/${accountId}/spam/policy`);
  }

  async putSpamPolicy(accountId: string, body: SpamPolicyBody) {
    return this.request<SpamPolicy>(`/accounts/${accountId}/spam/policy`, {
      method: 'PUT',
      body,
    });
  }

  // ── Audio page (PPV voice notes, ADR 0068) ─────────────────────────
  async getAudioPolicy(accountId: string) {
    return this.request<AudioPolicy>(`/accounts/${accountId}/audio/policy`, { noCache: true });
  }

  async putAudioPolicy(accountId: string, body: AudioPolicyBody) {
    return this.request<AudioPolicy>(`/accounts/${accountId}/audio/policy`, { method: 'PUT', body });
  }

  async getVoices(accountId: string) {
    return this.request<VoicesResponse>(`/accounts/${accountId}/audio/voices`, { noCache: true });
  }

  async getVoiceNoteQuota(accountId: string) {
    return this.request<VoiceNoteQuota>(`/accounts/${accountId}/audio/quota`, { noCache: true });
  }

  /** A short sample in the voice, as a blob URL the caller must revoke. */
  async getVoicePreviewUrl(accountId: string, kind: VoiceKind, voiceId: string) {
    const r = await this.rawFetch(`/accounts/${accountId}/audio/voices/${kind}/${voiceId}/preview`);
    return URL.createObjectURL(await r.blob());
  }

  async cloneVoice(accountId: string, label: string, files: File[]) {
    const form = new FormData();
    form.append('label', label);
    form.append('consent', 'true');
    files.forEach((f) => form.append('files', f, f.name));
    const r = await this.rawFetch(`/accounts/${accountId}/audio/custom-voices`, { method: 'POST', body: form });
    this._getCache.clear();
    return (await r.json()) as Voice;
  }

  async deleteCustomVoice(accountId: string, voiceId: string) {
    return this.request<void>(`/accounts/${accountId}/audio/custom-voices/${voiceId}`, { method: 'DELETE' });
  }

  async testVoiceNote(accountId: string, body: VoiceNoteTestBody) {
    return this.request<VoiceNoteTestResult>(`/accounts/${accountId}/audio/test`, { method: 'POST', body, timeoutMs: 150_000 });
  }

  // ── Voices page + Create audio (ADR 0028) ─────────────────────────
  async getVoiceLibrary() {
    return this.request<VoiceLibrary>('/audio/library', { noCache: true });
  }

  async getVoiceAccounts() {
    const res = await this.request<{ accounts: VoiceAccount[] }>('/audio/accounts', { noCache: true });
    return res.accounts;
  }

  /** One voice (or none) as the Account voice of many Accounts; answers their new rows. */
  async setAccountVoices(accountIds: string[], voice: { kind: VoiceKind; id: string } | null) {
    const res = await this.request<{ accounts: VoiceAccount[] }>('/audio/accounts/voice', {
      method: 'PUT',
      body: {
        account_ids: accountIds,
        voice_id: voice?.kind === 'stock' ? voice.id : null,
        custom_voice_id: voice?.kind === 'custom' ? voice.id : null,
      },
    });
    return res.accounts;
  }

  async getLibraryVoicePreviewUrl(kind: VoiceKind, voiceId: string) {
    const r = await this.rawFetch(`/audio/voices/${kind}/${voiceId}/preview`);
    return URL.createObjectURL(await r.blob());
  }

  async cloneLibraryVoice(label: string, files: File[]) {
    const form = new FormData();
    form.append('label', label);
    form.append('consent', 'true');
    files.forEach((f) => form.append('files', f, f.name));
    const r = await this.rawFetch('/audio/custom-voices', { method: 'POST', body: form });
    this._getCache.clear();
    return (await r.json()) as Voice;
  }

  async deleteLibraryVoice(voiceId: string) {
    return this.request<void>(`/audio/custom-voices/${voiceId}`, { method: 'DELETE' });
  }

  async getAccountVoice(accountId: string) {
    return this.request<AccountVoice>(`/accounts/${accountId}/audio/account-voice`, { noCache: true });
  }

  async createAudio(accountId: string, body: { text: string; fan_id: string; voice_register: VoiceRegister }) {
    return this.request<CreatedAudio>(`/accounts/${accountId}/audio/create`, { method: 'POST', body, timeoutMs: 150_000 });
  }

  async markAudioSent(accountId: string, recordingId: number, messageId: string | null) {
    return this.request<void>(`/accounts/${accountId}/audio/recordings/${recordingId}/sent`, {
      method: 'POST',
      body: { message_id: messageId },
    });
  }

  /** fetch() with the session token and the one-shot 401 refresh, for bodies `request`
   *  can't carry (multipart uploads, binary downloads). Throws the API's detail on error. */
  private async rawFetch(endpoint: string, init: RequestInit = {}): Promise<Response> {
    const send = (token: string | null) =>
      fetch(`${this.baseUrl}${endpoint}`, {
        ...init,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    let response = await send(this.token ?? localStorage.getItem('access_token'));
    if (response.status === 401) {
      const fresh = await this.refreshAccessToken();
      if (fresh) response = await send(fresh);
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: '' }));
      throw new Error(typeof error.detail === 'string' && error.detail ? error.detail : `HTTP error ${response.status}`);
    }
    return response;
  }

  async getCallPolicy(accountId: string) {
    return this.request<CallPolicy>(`/accounts/${accountId}/voice-call/policy`);
  }

  async putCallPolicy(accountId: string, body: CallPolicyBody) {
    return this.request<CallPolicy>(`/accounts/${accountId}/voice-call/policy`, {
      method: 'PUT',
      body,
    });
  }

  /** Classify (tag) up to `limit` still-untagged items in a vault via the qwen3-vl
   * module. Only classified content is priceable/sendable on-demand (ADR 0009). */
  async classifyVault(accountId: string, vaultId: string, limit = 25) {
    return this.request<VaultClassifyResult>(
      `/accounts/${accountId}/vaults/${encodeURIComponent(vaultId)}/classify?limit=${limit}`,
      { method: 'POST' }
    );
  }

  /** Start a durable, server-side classification run for one vault (tags + description).
   * Idempotent: if a run is already in flight it returns that run's status unchanged. */
  async analyseVault(accountId: string, vaultId: string) {
    return this.request<VaultAnalysisStatus>(
      `/accounts/${accountId}/content-requests/vaults/${encodeURIComponent(vaultId)}/analyse`,
      { method: 'POST' }
    );
  }

  /** Request cancellation of a vault's in-flight run. Cooperative: the worker winds down
   * on its next heartbeat and marks the run 'cancelled'. Returns the run's current status. */
  async stopVaultAnalysis(accountId: string, vaultId: string) {
    return this.request<VaultAnalysisStatus>(
      `/accounts/${accountId}/content-requests/vaults/${encodeURIComponent(vaultId)}/analyse/stop`,
      { method: 'POST' }
    );
  }

  /** Start a run for every attached vault with untagged items. */
  async analyseAllVaults(accountId: string) {
    return this.request<VaultAnalysisStatus[]>(
      `/accounts/${accountId}/content-requests/analyse-all`,
      { method: 'POST' }
    );
  }

  /** Latest classification-run status per attached vault (for load + polling). */
  async getAnalysisStatus(accountId: string) {
    return this.request<AnalysisStatusResponse>(
      `/accounts/${accountId}/content-requests/analysis-status`
    );
  }

  /** Paginated media in a vault, each with its classification, for the content-grid widget. */
  async getVaultContent(accountId: string, vaultId: string, offset = 0, limit = 48) {
    return this.request<VaultContentPage>(
      `/accounts/${accountId}/content-requests/vaults/${encodeURIComponent(vaultId)}/content?offset=${offset}&limit=${limit}`
    );
  }

  /** Counts + cover thumbs for the given vaults in one call (empty for Fansly). */
  async getVaultCovers(accountId: string, vaultIds: string[]) {
    return this.request<VaultCover[]>(
      `/accounts/${accountId}/content-requests/vault-covers?ids=${encodeURIComponent(vaultIds.join(','))}`
    );
  }

  /** Lazy per-vault item / classification counts (fetched after the vault list renders). */
  async getVaultCounts(accountId: string, vaultId: string) {
    return this.request<VaultCounts>(
      `/accounts/${accountId}/content-requests/vaults/${encodeURIComponent(vaultId)}/counts`
    );
  }

  /** Classify ONE item on demand (from the content-detail widget). Always re-runs. The
   * media metadata lets the server pull a not-yet-local item into of_content first. */
  async classifyContentItem(
    accountId: string,
    contentId: string,
    media?: { vault_id?: string; type?: string | null; thumb_url?: string | null; full_url?: string | null; duration?: number | null }
  ) {
    return this.request<VaultContentItem>(
      `/accounts/${accountId}/content-requests/content/${encodeURIComponent(contentId)}/classify`,
      { method: 'POST', body: media ?? {} }
    );
  }

  /** Classification + in-flight analysis state for a set of items, from the DB only
   * (no OnlyFans call). Media fields come back empty — keep the live urls you have. */
  async getContentAnalysisStatus(accountId: string, ids: string[]) {
    return this.request<VaultContentItem[]>(
      `/accounts/${accountId}/content-requests/content/status`,
      { method: 'POST', body: { ids } }
    );
  }

  /** Operator notes and Badges for a page of Vault items — only ids that have them come back. */
  async getVaultNotes(accountId: string, ids: string[]) {
    return this.request<{ notes: Record<string, string>; badges?: Record<string, number[]> }>(
      `/accounts/${accountId}/vault/notes/query`,
      { method: 'POST', body: { ids } }
    );
  }

  /** The Account's Vault Badges in creation order, each with how many items carry it. */
  async getVaultBadges(accountId: string) {
    const res = await this.request<{ badges: VaultBadge[] }>(`/accounts/${accountId}/vault/badges`);
    return res.badges;
  }

  async createVaultBadge(accountId: string, name: string, color: VaultBadge['color']) {
    return this.request<VaultBadge>(`/accounts/${accountId}/vault/badges`, {
      method: 'POST',
      body: { name, color },
    });
  }

  async updateVaultBadge(accountId: string, badgeId: number, patch: { name?: string; color?: VaultBadge['color'] }) {
    return this.request<VaultBadge>(`/accounts/${accountId}/vault/badges/${badgeId}`, {
      method: 'PATCH',
      body: patch,
    });
  }

  async deleteVaultBadge(accountId: string, badgeId: number) {
    await this.request<void>(`/accounts/${accountId}/vault/badges/${badgeId}`, { method: 'DELETE' });
  }

  /** Attach a Badge to `add` and detach it from `remove`; returns the badge with its new count. */
  async setVaultBadgeItems(accountId: string, badgeId: number, add: string[], remove: string[]) {
    return this.request<VaultBadge>(`/accounts/${accountId}/vault/badges/${badgeId}/items`, {
      method: 'POST',
      body: { add, remove },
    });
  }

  /** Set one Vault item's operator notes; blank clears them. Returns what was saved. */
  async saveVaultNotes(accountId: string, contentId: string, notes: string) {
    const res = await this.request<{ id: string; operator_notes: string | null }>(
      `/accounts/${accountId}/vault/notes/${encodeURIComponent(contentId)}`,
      { method: 'PUT', body: { operator_notes: notes } }
    );
    return res.operator_notes ?? '';
  }

  /** Persist an operator's hand-edited classification for one item. */
  async updateContentClassification(
    accountId: string,
    contentId: string,
    body: ClassificationEditBody
  ) {
    return this.request<VaultContentItem>(
      `/accounts/${accountId}/content-requests/content/${encodeURIComponent(contentId)}/classification`,
      { method: 'PUT', body }
    );
  }

  /** Persist an item's PPV-preview list + exclusion flags (ADR 0031). Auto-sets
   * is_preview_content on the referenced preview items server-side. */
  async updateContentFlags(
    accountId: string,
    contentId: string,
    body: ContentFlagsBody
  ) {
    return this.request<VaultContentItem>(
      `/accounts/${accountId}/content-requests/content/${encodeURIComponent(contentId)}/flags`,
      { method: 'PUT', body }
    );
  }

  /** ADR 0034 — list the OF creators this account is eligible to tag (the rfTag
   * allow-list surfaced by OnlyFans). Live via the account's session, so a logged-out
   * account returns 409. Fansly returns an empty list. */
  async listTaggableUsers(
    accountId: string,
    opts: { q?: string; limit?: number; offset?: number } = {}
  ) {
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.offset != null) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return this.request<TaggableUsersPage>(
      `/accounts/${accountId}/content-requests/taggable-users${qs ? `?${qs}` : ''}`,
    );
  }

  // ===== Fixed sales ladders (ADR 0005) =====

  async listFixedLadders(accountId: string) {
    return this.request<FixedLadder[]>(`/accounts/${accountId}/fixed-sales-ladders`);
  }

  async createFixedLadder(accountId: string, body: FixedLadderBody) {
    return this.request<FixedLadder>(`/accounts/${accountId}/fixed-sales-ladders`, {
      method: 'POST',
      body,
    });
  }

  async updateFixedLadder(accountId: string, ladderId: number, body: FixedLadderBody) {
    return this.request<FixedLadder>(
      `/accounts/${accountId}/fixed-sales-ladders/${ladderId}`,
      { method: 'PUT', body }
    );
  }

  async deleteFixedLadder(accountId: string, ladderId: number) {
    return this.request<{ ok: boolean; deleted: number }>(
      `/accounts/${accountId}/fixed-sales-ladders/${ladderId}`,
      { method: 'DELETE' }
    );
  }

  // ===== Tips policies =====

  async getHotTipPolicy(accountId: string) {
    return this.request<HotTipPolicy>(`/accounts/${accountId}/tips/hot-policy`);
  }

  async putHotTipPolicy(accountId: string, body: HotTipPolicyBody) {
    return this.request<HotTipPolicy>(`/accounts/${accountId}/tips/hot-policy`, {
      method: 'PUT',
      body,
    });
  }

  async getNormalTipPolicy(accountId: string) {
    return this.request<NormalTipPolicy>(`/accounts/${accountId}/tips/normal-policy`);
  }

  async putNormalTipPolicy(accountId: string, body: NormalTipPolicyBody) {
    return this.request<NormalTipPolicy>(`/accounts/${accountId}/tips/normal-policy`, {
      method: 'PUT',
      body,
    });
  }

  async getVipOfferPolicy(accountId: string) {
    return this.request<VipOfferPolicy>(`/accounts/${accountId}/tips/vip-policy`);
  }

  async putVipOfferPolicy(accountId: string, body: VipOfferPolicyBody) {
    return this.request<VipOfferPolicy>(`/accounts/${accountId}/tips/vip-policy`, {
      method: 'PUT',
      body,
    });
  }

  async getSubOfferPolicy(accountId: string) {
    return this.request<SubOfferPolicy>(`/accounts/${accountId}/tips/sub-policy`);
  }

  async putSubOfferPolicy(accountId: string, body: SubOfferPolicyBody) {
    return this.request<SubOfferPolicy>(`/accounts/${accountId}/tips/sub-policy`, {
      method: 'PUT',
      body,
    });
  }

  async getPpvOfferPolicy(accountId: string) {
    return this.request<PpvOfferPolicy>(`/accounts/${accountId}/tips/ppv-policy`);
  }

  async putPpvOfferPolicy(accountId: string, body: PpvOfferPolicyBody) {
    return this.request<PpvOfferPolicy>(`/accounts/${accountId}/tips/ppv-policy`, {
      method: 'PUT',
      body,
    });
  }

  async getPaidSextingPolicy(accountId: string) {
    return this.request<PaidSextingPolicy>(`/accounts/${accountId}/tips/paid-policy`);
  }

  async putPaidSextingPolicy(accountId: string, body: PaidSextingPolicyBody) {
    return this.request<PaidSextingPolicy>(`/accounts/${accountId}/tips/paid-policy`, {
      method: 'PUT',
      body,
    });
  }

  // ===== Content phase (unified life/teasing/sales) =====

  async listAttachedContentVaults(accountId: string, phase: ContentPhase) {
    return this.request<ContentPhaseAttachedVault[]>(
      `/accounts/${accountId}/content-phase/${phase}/vaults`
    );
  }

  async attachContentVault(accountId: string, phase: ContentPhase, vaultId: string) {
    return this.request<{ ok: boolean }>(
      `/accounts/${accountId}/content-phase/${phase}/vaults/${encodeURIComponent(vaultId)}/attach`,
      { method: 'POST' }
    );
  }

  async detachContentVault(accountId: string, phase: ContentPhase, vaultId: string) {
    return this.request<{ ok: boolean }>(
      `/accounts/${accountId}/content-phase/${phase}/vaults/${encodeURIComponent(vaultId)}/detach`,
      { method: 'POST' }
    );
  }

  async fetchContentVaultMedia(
    accountId: string,
    vaultId: string,
    offset: number,
    limit: number
  ) {
    return this.request<SalesMediaPage>(
      `/accounts/${accountId}/content-phase/vaults/${encodeURIComponent(vaultId)}/media`,
      { method: 'POST', body: { offset, limit } }
    );
  }

  async updateContentItem(
    accountId: string,
    contentId: string,
    body: ContentItemUpdateBody
  ) {
    return this.request<SalesMediaItem>(
      `/accounts/${accountId}/content-phase/items/${encodeURIComponent(contentId)}`,
      { method: 'PATCH', body }
    );
  }

  async generateContentItemDescription(
    accountId: string,
    contentId: string,
    body: { thumb_url?: string | null; video_url?: string | null; type?: string | null; phase?: string | null }
  ) {
    return this.request<{ description: string | null; sales_stage: string | null; remaining_generations: number }>(
      `/accounts/${accountId}/content-phase/items/${encodeURIComponent(contentId)}/generate-description`,
      { method: 'POST', body }
    );
  }

  /** Returns an absolute URL suitable for `<img src>` that pipes the OF media
   * through our backend → the account proxy. Auth: `Authorization` header on
   * normal API calls comes from the JWT, but `<img>` cannot send custom
   * headers, so we attach the access token in the query string instead. The
   * backend route accepts `?token=<jwt>` as a fallback (added below). */
  proxyMediaUrl(accountId: string, mediaUrl: string): string {
    const params = new URLSearchParams({ url: mediaUrl });
    if (this.token) params.set('token', this.token);
    return `${this.baseUrl}/accounts/${accountId}/sales/proxy-media?${params.toString()}`;
  }

  async validatePromo(code: string, subtotalCents: number) {
    const params = new URLSearchParams({
      code,
      subtotal_cents: String(subtotalCents),
    });
    return this.request<{
      code: string;
      valid: boolean;
      reason?: string;
      discount_cents: number;
      wallet_credit_cents: number;
      /** 'promo' or 'referral' — the shared field accepts either. */
      kind?: 'promo' | 'referral' | null;
    }>(`/payments/promo/validate?${params.toString()}`, {
      method: 'POST',
    });
  }

  // ===== Referrals =====

  async getReferralSummary() {
    return this.request<ReferralSummary>('/referrals/summary');
  }

  async regenerateReferralCode() {
    return this.request<{ code: string }>('/referrals/code/regenerate', { method: 'POST' });
  }

  async updateReferralWallet(address: string | null) {
    return this.request<{ payout_wallet_address: string | null; payout_wallet_network: string }>(
      '/referrals/wallet',
      { method: 'PUT', body: { address } }
    );
  }

  // ===== Statistics (daily chat stats) =====

  /**
   * Range totals of the chat-stats counters. Omit accountId for the whole
   * permitted fleet (accounts the user owns or has stats access on); pass it for
   * a single account. Dates are inclusive Kyiv calendar days (YYYY-MM-DD);
   * omitting both defaults to the last 7 days.
   */
  async getChatStats(params: { start?: string; end?: string; accountId?: string } = {}) {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    if (params.accountId) query.set('account_id', params.accountId);
    const qs = query.toString();
    return this.request<ChatStatsResponse>(`/chat-stats${qs ? `?${qs}` : ''}`);
  }

  /**
   * Unified Statistics: Revenue (every stream, gross + net), who sold the part
   * sold in chat, per-day / per-account splits and the Team rows. Money days are
   * UTC days; scope is the same as getChatStats.
   */
  async getStatistics(params: { start?: string; end?: string; accountId?: string } = {}) {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    if (params.accountId) query.set('account_id', params.accountId);
    const qs = query.toString();
    return this.request<StatisticsResponse>(`/statistics${qs ? `?${qs}` : ''}`);
  }

  /** Whether the user may see Statistics for at least one account. */
  async getChatStatsAccess() {
    return this.request<{ has_access: boolean }>('/chat-stats/access');
  }

  // --- Bulk Media (ADR 0025/0026): super admins only before launch -----------------

  async getBulkMediaAccounts() {
    const res = await this.request<{ accounts: BulkMediaAccount[] }>('/bulk-media/accounts');
    return res.accounts;
  }

  /** Every Category name on these Accounts, read live from OnlyFans. */
  async getBulkMediaCategories(accountIds: string[]) {
    return this.request<CategoryOverview>('/bulk-media/categories/overview', {
      method: 'POST',
      body: { account_ids: accountIds },
      timeoutMs: 120_000,
    });
  }

  /** Reserve a staged file; its chunks then go up with bulkMedia/api putChunk. */
  async createBulkMediaFile(body: { filename: string; content_type: string; size: number }) {
    return this.request<NewStagedFile>('/bulk-media/files', { method: 'POST', body });
  }

  async completeBulkMediaFile(fileId: string) {
    return this.request<{ file_id: string; status: string; etag: string }>(`/bulk-media/files/${fileId}/complete`, {
      method: 'POST',
    });
  }

  async discardBulkMediaFile(fileId: string) {
    return this.request<{ ok: boolean }>(`/bulk-media/files/${fileId}`, { method: 'DELETE' });
  }

  async createBulkMediaJob(body: NewJob) {
    return this.request<JobDetail>('/bulk-media/jobs', { method: 'POST', body });
  }

  /** The last 7 days of jobs, everyone's. */
  async getBulkMediaJobs() {
    const res = await this.request<{ jobs: JobSummary[] }>('/bulk-media/jobs', { noCache: true });
    return res.jobs;
  }

  async getBulkMediaJob(jobId: string) {
    return this.request<JobDetail>(`/bulk-media/jobs/${jobId}`, { noCache: true });
  }

  async cancelBulkMediaJob(jobId: string) {
    return this.request<JobDetail>(`/bulk-media/jobs/${jobId}/cancel`, { method: 'POST' });
  }

  async retryBulkMediaJob(jobId: string) {
    return this.request<JobDetail>(`/bulk-media/jobs/${jobId}/retry`, { method: 'POST' });
  }
}

// Export singleton instance
export const api = new ApiClient(config.apiUrl);
